import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { X, Upload, Play, Link2, Sparkles, CheckSquare, ChevronDown, ChevronRight, Check, Lock, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button, EmptyState, AiRunModal, AiPromptInput } from '../common';
import PanelCloseButton from '../common/PanelCloseButton';
import {
  getProjectDetail,
  getOcrStatus,
  uploadProjectFile,
  runProject,
  patchProject,
  bindProject,
  analyzeProject,
  getAnalysisStatus,
  promoteProject,
  // closeProject 는 ClosePaperModal 이 부른다 — 시험지 만들기와 종료를 한 흐름으로 잇기 위해서다
  reopenProject,
  getSkipReasonText,
  getProjectStatusDisplay,
  getFileKindDisplay,
  getFileStatusDisplay,
  fetchAllProjectProblems,
  fetchProjectPages,
  applyNumberSteps,
  freezeOcrLabels,
  OCR_FILE_KIND,
} from './ocrApi';
import { planReindexByOrder, planApplySteps } from './numberPlan';
import useOcrProblemList from './useOcrProblemList';
import useReviewDeepLink from './useReviewDeepLink';
import OcrReviewSection from './OcrReviewSection';
import OcrReviewModal from './OcrReviewModal';
import PromoteLabelModal from './PromoteLabelModal';
import FailedPagesList from './FailedPagesList';
import { trackRecoveredPages, promptsForTargets } from './runRules';
import ClosePaperModal from './ClosePaperModal';
import { PromotedProblemLinks, ResultPaperRow } from './OcrResultLinks';
import OcrProgressCard from './OcrProgressCard';
import AnalysisMap from './AnalysisMap';
import OcrUsageCard from './OcrUsageCard';

/**
 * OCR 프로젝트 디테일 패널 (FE-2 — FE-1 placeholder 교체).
 *
 * 상단: 프로젝트명 + 상태 배지 + 진행 요약(파일·분리·OCR 진행률 바)
 * 본문: 파일 목록 테이블 → 업로드 영역(파일별 종류 지정, 순차 업로드) → 처리 실행(+ 페이지 범위 옵션)
 *
 * OCR 진행률은 5초 폴링(getOcrStatus) — pending/processing이 남아 있는 동안만 돌고,
 * 데몬 미가동 시 pending에 머무르는 것도 그대로 표시한다(지시서 §6, 프론트는 진행률만 읽음).
 * 닫기 버튼은 학생·시험지 상세처럼 헤더 오른쪽 끝에 둔다.
 */

const POLL_MS = 5000;

// 승격 결과의 skipped[]를 사유별 그룹 접이식으로 표시 (FE-5 §2-1)
function PromoteSkippedGroups({ skipped }) {
  const [open, setOpen] = useState(false);
  if (!Array.isArray(skipped) || skipped.length === 0) return null;
  const groups = new Map();
  skipped.forEach((s) => {
    const key = getSkipReasonText(s?.reason);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(s?.problem_number ?? s?.ocr_prob_id);
  });
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700"
      >
        {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        건너뜀 {skipped.length}건 (사유별)
      </button>
      {open && [...groups.entries()].map(([reason, nums]) => (
        <div key={reason} className="ml-5 mt-1 text-xs text-gray-600">
          <span className="font-medium">{reason}</span>: 문제 {nums.join(', ')}
        </div>
      ))}
    </div>
  );
}

// "1-30" | "5" → 0-base [s, e] | null. 잘못된 형식과 0 이하는 undefined 반환(호출부에서 에러 표시).
//
// 계약 확정(BE 회신 ocr-run-page-range-backend-reply.md, 커밋 d773f3b): page_range 는
// **0부터·양끝 포함**이고 [0,0] = 첫 장 하나다. 사람은 1부터 세므로 여기서 -1 해 보낸다.
// 과거 「[0,0] 이 전체 실행」 사고의 원인은 축이 아니라 분리(split)가 페이지를 전부 「OCR
// 대기(100)」로 만들던 서버 버그였다 — 수정되어 range 부분 실행이 실제로 동작한다(실측).
// 무효 범위(타입 불일치·음수·역순)는 서버가 400으로 거른다.
function parsePageRange(text) {
  const t = String(text || '').trim();
  if (!t) return null;
  const m = t.match(/^(\d+)\s*[-~]\s*(\d+)$/) || t.match(/^(\d+)$/);
  if (!m) return undefined;
  const s = parseInt(m[1], 10);
  const e = parseInt(m[2] ?? m[1], 10);
  if (Number.isNaN(s) || Number.isNaN(e) || s > e) return undefined;
  if (s < 1) return undefined; // 사람 입력은 1부터 — 0은 오타다
  return [s - 1, e - 1]; // 사람 쪽수(1-base) → 서버 축(0-base)
}

// 서버가 실제로 쪼갠 쪽 번호(page_index, 0부터) 목록 → 사람이 읽는 쪽 범위.
// [0,1,2,3,4,5] → "1–6" · [11,12,13,19] → "12–14, 20"
//
// 이 목록이 「이 파일에서 무엇을 읽었는가」의 유일한 사실이다. 파일 행의 분모(total_pages)는
// 파일이 원래 몇 쪽인지가 아니라 지금까지 쪼갠 쪽수라, 일부만 돌려도 언제나 "N/N"으로 보인다.
// 그래서 분모로는 부분 실행을 구별할 수 없고, 쪽 번호를 직접 봐야 한다.
function formatPageRanges(indexes) {
  const sorted = [...new Set(indexes)].filter((n) => Number.isInteger(n) && n >= 0).sort((a, b) => a - b);
  if (sorted.length === 0) return null;
  const groups = [];
  let start = sorted[0];
  let prev = sorted[0];
  for (let i = 1; i < sorted.length; i += 1) {
    if (sorted[i] === prev + 1) { prev = sorted[i]; continue; }
    groups.push([start, prev]);
    start = sorted[i];
    prev = sorted[i];
  }
  groups.push([start, prev]);
  // 표시는 사람 쪽수(+1)로 — 검수 모달·사용량 카드와 같은 축이다
  const parts = groups.map(([a, b]) => (a === b ? `${a + 1}` : `${a + 1}–${b + 1}`));
  return {
    parts,
    text: parts.join(', '),
    // 1쪽부터 끊김 없이 이어지는가. 그렇더라도 「뒤에 더 있는지」는 알 수 없다 — 파일 전체 쪽수를
    // 서버가 주지 않기 때문이다. 그래서 이 경우에는 「일부」라고 단정하지 않고 범위만 말한다.
    contiguousFromFirst: groups.length === 1 && groups[0][0] === 0,
    count: sorted.length,
  };
}

// { [file_id]: { indexes, truncated } | null } → 배지가 쓰는 표시용 값으로 옮긴다.
// undefined = 「모름」(조회 실패) — 그 파일은 서버 상태 배지로 폴백한다.
function toFilePagesView(raw) {
  const out = {};
  for (const fid of Object.keys(raw)) {
    const got = raw[fid];
    if (!got) { out[fid] = undefined; continue; }
    const r = formatPageRanges(got.indexes);
    out[fid] = r ? { ...r, truncated: got.truncated, indexes: got.indexes, prompts: got.prompts || {} } : null;
  }
  return out;
}

// 실행 전후의 page_index 집합을 비교해 '이번 실행이 새로 쪼갠 쪽'만 뽑는다(0 기반 그대로).
// 한쪽이라도 모르면(조회 실패·2000쪽 상한으로 잘림) null을 돌려 「모른다」고 말한다 —
// 부분 정보로 "N쪽을 새로 읽었다"고 말하면 그 문장 자체가 거짓이 된다.
function diffNewPages(before, after) {
  const byFile = {};
  let total = 0;
  for (const fid of Object.keys(after)) {
    const a = after[fid];
    const b = before[fid];
    if (!a || !b || a.truncated || b.truncated) return null;
    const seen = new Set(b.indexes);
    const fresh = a.indexes.filter((i) => !seen.has(i));
    // 파일별로 센다 — 파일이 둘이면 양쪽의 0쪽이 같은 번호라, 합쳐서 세면 2장이 1장으로 줄어든다
    if (fresh.length > 0) { byFile[fid] = fresh; total += fresh.length; }
  }
  return { total, byFile };
}

// 실행 결과를 사람 문장으로 만든다. 「몇 개」가 아니라 「어느 쪽」을 말한다 —
// 2026-08-23에 한 장만 요청한 실행이 책 전체를 읽었는데, 화면이 개수만 말해 그 사실을 숨겼다.
//
// targetIds = 이번 실행이 대상으로 삼은 file_id 목록(문자열). 파일을 고르지 않았으면 전체 파일이다.
// 재는 것은 언제나 전체 파일이므로(runPages), **대상 밖 파일에서 쪽이 늘었다면 서버가 file_id를
// 무시했다는 뜻**이다. 서버가 file_id로 파일을 좁혀 준다는 것은 계약 주석 한 줄(ocrApi.js:242)이
// 전부이고 실제로 그렇게 동작한 관측이 저장소에 없다 — 그래서 화면이 매 실행 지켜본다.
// nameOf(id) = file_id → 사람이 읽는 파일 이름.
function buildRunMessage({ pageRange, queued, newPages, targetIds, nameOf, skipped = 0 }) {
  // 대기열 0 이면 이유를 붙인다(F9 끝 — BE-20260925 §3). 서버는 완료·처리중 쪽을 건너뛰고 그 수를 skipped_pages 로 준다.
  const zeroWhy = queued === 0
    ? (skipped > 0 ? ` — 요청한 쪽 ${skipped}개는 이미 읽었거나 읽는 중이라 건너뛰었습니다` : ' — 요청한 범위에 다시 읽을 쪽이 없습니다')
    : '';
  const queuedText = `OCR 대기열 ${queued}페이지${zeroWhy}`;
  if (newPages === null) {
    return {
      type: 'info',
      step: 2,
      text: `분리 완료 — ${queuedText}. 새로 쪼갠 쪽은 확인하지 못했습니다(페이지 목록을 읽지 못했습니다).`,
    };
  }
  if (newPages.total === 0) {
    return {
      type: 'info',
      step: 2,
      text: `분리 완료 — 새로 쪼갠 쪽이 없습니다. 요청한 쪽은 이미 분리돼 있습니다 · ${queuedText}.`,
    };
  }
  // 파일이 하나일 때만 쪽 번호를 짚는다. 여럿이면 같은 번호가 파일마다 있어 범위가 뜻을 잃는다.
  const ids = Object.keys(newPages.byFile);
  const one = ids.length === 1 ? formatPageRanges(newPages.byFile[ids[0]]) : null;
  const where = one ? `${one.text}쪽` : `${newPages.total}쪽(파일 ${ids.length}개)`;

  // ① 대상 밖 파일이 쪼개졌는가 — 「몇 쪽인가」보다 먼저 본다.
  // 수량이 맞아도 자리가 틀리면 그건 다른 사고다: 「이 파일 1쪽」을 요청했는데 파일 셋이 1쪽씩
  // 쪼개지면 합계는 3쪽이라 아래 ②의 기대치(1쪽×대상 1파일)에도 걸리지만, 파일을 지정하지 않은
  // 전체 실행에서는 기대치가 3쪽이 되어 조용히 통과한다. 그래서 자리를 따로 묻는다.
  const outside = targetIds.length > 0 ? ids.filter((id) => !targetIds.includes(id)) : [];
  if (outside.length > 0) {
    const outCount = outside.reduce((a, id) => a + (newPages.byFile[id]?.length ?? 0), 0);
    const outNames = outside.map((id) => nameOf(id)).join(', ');
    const target = targetIds.length === 1 ? `「${nameOf(targetIds[0])}」 한 파일` : `파일 ${targetIds.length}개`;
    return {
      type: 'error',
      step: 2,
      text: `대상은 ${target}이었는데 ${outNames}에서도 ${outCount}쪽이 새로 쪼개졌습니다`
        + `(이번 실행 합계 ${newPages.total}쪽). 서버가 file_id를 무시했을 수 있습니다 — ${queuedText}. `
        + '의도한 실행이 아니라면 백엔드에 대기열 정리를 요청하세요(화면에는 취소 수단이 없습니다).',
    };
  }

  // ② 요청한 것보다 많이 쪼개졌다면 서버가 범위를 다르게 해석한 것이다 — 오늘 사고의 모양이 이것이다.
  // 범위는 파일마다 적용되므로(file_id 없으면 전체 파일) 기대치에 대상 파일 수를 곱한다.
  const perFile = pageRange ? (pageRange[1] - pageRange[0] + 1) : null;
  const expected = perFile == null ? null : perFile * Math.max(1, targetIds.length);
  if (expected != null && newPages.total > expected) {
    return {
      type: 'error',
      step: 2,
      text: `요청한 범위는 ${expected}쪽인데 ${newPages.total}쪽이 새로 쪼개졌습니다(${where}). `
        + `서버가 범위를 다르게 해석했을 수 있습니다 — ${queuedText}. `
        + '의도한 실행이 아니라면 백엔드에 대기열 정리를 요청하세요(화면에는 취소 수단이 없습니다).',
    };
  }
  return {
    type: 'info',
    step: 2,
    text: `분리 완료 — ${where}을 새로 쪼갰습니다 · ${queuedText}. `
      + 'OCR은 백그라운드에서 처리되며, 진행 상황은 위 진행률에서 확인할 수 있습니다.',
  };
}

// per_page 100 × 20 = 2000쪽까지 훑는다. 넘으면 truncated로 알린다(fetchAllProjectPages와 같은 상한).
const FILE_PAGES_MAX_REQUESTS = 20;

// 파이프라인 7단계 키(위→아래 진행 순서). status 상태머신에 매핑.
const STEP_KEYS = ['upload', 'run', 'bind', 'review', 'analyze', 'promote', 'close'];

// 프로젝트 상세의 진행 집계 6종을 화면이 쓰는 이름으로 옮긴다(백엔드 커밋 6189a31).
// 이 여섯이 오기 전에는 문제 전량을 훑어 직접 셌다 — 프로젝트당 ceil(문제수/100) 왕복이
// 승인·반려·매칭·승격 뒤마다 반복됐다. 이제 상세 한 번으로 끝난다.
// 필드가 없으면(구버전 서버) null을 돌려 판정이 폴백을 쓰게 한다 — 0으로 채우면 '할 일 없음'이 된다.
function deriveStats(detail) {
  if (!detail || detail.problem_count == null) return null;
  return {
    total: detail.problem_count ?? 0,
    reviewPending: detail.review_pending_count ?? 0,   // r100 & a≠200 — 원본 미검수
    rereviewPending: detail.rereview_pending_count ?? 0, // r100 & a=200 — AI가 되돌려 보낸 것
    reviewDone: detail.review_done_count ?? 0,
    rejected: detail.rejected_count ?? 0,
    promoted: detail.promoted_count ?? 0,
  };
}

// 현재 "지금 할 일" 단계 도출 — detail.status + OCR 진행률(bind_available) + 분석 현황 + 진행 집계.
//
// '지금 할 일'은 사람이 할 일이다. 매칭 이후 구간(④⑤⑥)의 순서는 이렇다:
//   1. 원본 미검수가 남았으면 검수 — 원본 대조가 AI 분석의 입력이라 순서가 뒤집히면 안 된다.
//   2. 분석이 돌거나 재검수 대기가 있으면 분석 — AI가 만든 것을 사람이 확인하는 구간까지가 ⑤다.
//   3. 검수는 끝났는데 분석을 한 번도 안 돌렸으면 분석.
//   4. 승격할 것이 남았으면 승격.
// 이 구간은 진행 집계(stats)가 있어야 판정할 수 있다. status 400은 '승격 가능한 국면'이지
// '승격 완료'가 아니라서(백엔드 확인), status로는 실제보다 진도를 앞서 읽는다.
// stats가 없으면(구버전 서버 — 집계 6종 미탑재) 예전 근사 판정으로 물러선다.
function deriveCurrentStep(detail, ocrStatus, analysisStatus = null, stats = null) {
  const rereviewPending = analysisStatus?.rereview_pending ?? 0;
  // all_done은 '진행 중 작업 없음'일 뿐이라 한 건도 못 했거나 실패가 남으면 완료로 보지 않는다
  const analysisDone = !!analysisStatus?.all_done
    && (analysisStatus.done ?? 0) > 0
    && (analysisStatus.failed ?? 0) === 0;
  // 분석이 '지금 돌고 있는가'는 status로 판단하지 않는다 — 350은 분석이 끝나도 승격 전까지 유지된다.
  // 대기·처리 중 건수가 사실이다.
  const analysisRunning = (analysisStatus?.pending ?? 0) + (analysisStatus?.processing ?? 0) > 0;
  const analysisRan = (analysisStatus?.done ?? 0) > 0 || (analysisStatus?.failed ?? 0) > 0;
  if (!detail) return 'upload';
  const s = detail.status ?? 0;
  if (s >= 500) return null; // 종료 — 현재 단계 없음(전부 완료)
  const files = Array.isArray(detail.files) ? detail.files : [];
  const hasFiles = files.length > 0 || (detail.file_count ?? 0) > 0;
  const bound = s >= 300;
  // 대기/처리 중 페이지가 남아 있으면 매칭 완료(bound) 이후라도 OCR 미완료 — 파일 추가 후 재-OCR 구간
  const ocrPending = (ocrStatus?.pending ?? 0) + (ocrStatus?.processing ?? 0);
  const ocrComplete = ocrPending === 0 && (bound || !!ocrStatus?.bind_available);
  if (!hasFiles) return 'upload';
  if (!ocrComplete) return 'run';
  if (!bound) return 'bind';
  if (stats) {
    if (stats.reviewPending > 0) return 'review';
    if (analysisRunning || stats.rereviewPending > 0) return 'analyze';
    // 검수는 끝났다. 분석을 아직 한 번도 안 돌렸으면 다음에 누를 것이 그것이다
    // (분석 없이 승격하는 것도 가능하지만, 파이프라인 순서는 분석이 먼저다).
    if (!analysisRan && stats.reviewDone > 0) return 'analyze';
    if (stats.reviewDone > stats.promoted) return 'promote';
    return 'close';
  }
  // ── 폴백: 집계를 모를 때의 근사 ──
  // total_promoted는 상세에 없는 필드다 — promote/close 응답 전용이었다(백엔드 확인).
  // 여기서는 status만 본다: 400 이상이면 최초 승격이 일어난 뒤다(350→400 전이 = 최초 승격 시점).
  const promoted = s >= 400;
  if (analysisRunning || rereviewPending > 0) return 'analyze';
  if (analysisDone && !promoted) return 'promote';
  if (!promoted) return 'review';
  return 'close';
}

// 단계 번호 → 펼침 키. actionMsg.step 이 어느 단계의 결과인지 되짚는 데 쓴다
// (StepRow 의 n 과 isOpen 의 key 를 잇는 유일한 자리 — 새 단계를 넣으면 여기도 채운다).
const STEP_KEY = {
  1: 'upload', 2: 'run', 3: 'bind', 4: 'review', 5: 'analyze', 6: 'promote', 7: 'close',
};

const STEP_BADGE = {
  done: 'bg-green-100 text-green-700',
  current: 'bg-primary text-white',
  available: 'border-2 border-gray-300 bg-white text-gray-600',
  locked: 'border-2 border-gray-200 bg-gray-50 text-gray-300',
  // 끝났지만 그대로 두면 안 되는 것 — 등록 없이 종료된 프로젝트의 ⑥(prod #35, BE-20260925 §3 「#35 화면」).
  // 초록 체크면 정상 종료처럼 읽힌다. 호박색은 이 화면에서 「사람이 할 일이 남았다」의 색이다.
  warn: 'bg-amber-100 text-amber-700',
};

// 세로 스텝 카드 한 행 — 헤더(번호 배지·제목·요약·펼침) + 펼침 시 본문.
// 현재 단계는 자동 펼침, 완료/이후(available) 단계는 클릭해 상세보기, 미도달(locked)은 잠금.
function StepRow({ n, title, state, summary, expandable, open, onToggle, children }) {
  const dim = state === 'locked';
  return (
    <div className={state === 'current' ? 'bg-primary/[0.04]' : ''}>
      {/* WAI-ARIA 아코디언 관례 — heading > button. 미도달(locked) 단계는 disabled로 탭 순서에서 제외 */}
      <h3>
        <button
          type="button"
          onClick={expandable ? onToggle : undefined}
          disabled={!expandable}
          aria-expanded={expandable ? open : undefined}
          className={`flex w-full items-center gap-3 px-4 py-3 text-left ${expandable ? 'cursor-pointer hover:bg-gray-50/60' : 'cursor-default'}`}
        >
          <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${STEP_BADGE[state]}`}>
            {state === 'done' ? <Check className="h-4 w-4" /> : state === 'locked' ? <Lock className="h-3.5 w-3.5" /> : state === 'warn' ? <AlertTriangle className="size-3.5" /> : n}
          </span>
          <span className={`shrink-0 text-sm font-bold ${dim ? 'text-gray-400' : 'text-gray-900'}`}>{title}</span>
          {state === 'current' && (
            <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">지금 할 일</span>
          )}
          <span className={`ml-auto min-w-0 truncate text-xs ${dim ? 'text-gray-300' : 'text-gray-500'}`} title={summary}>
            {summary}
          </span>
          {expandable && (
            <ChevronDown className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
          )}
        </button>
      </h3>
      {expandable && open && <div className="px-4 pb-4 pl-14">{children}</div>}
    </div>
  );
}

// 스텝 본문 하단의 액션 바 — 일곱 단계가 같은 배치를 쓴다.
// 위: 상태(aside)와 설명(hint)이 한 줄을 통째로 갖는다 · 아래: 액션 버튼(children), 오른쪽 끝.
// 한때 이 둘을 좌우로 나란히 놨는데, 그러면 글과 입력칸과 버튼이 세 칸으로 갈려
// 문장이 좁은 기둥에 갇혔고 창 폭에 따라 몇 줄이 될지가 매번 달라졌다.
// message: 이 단계의 실행 결과 { type: 'info'|'error', text }.
// 안내(hint)와 같은 자리를 쓴다 — 버튼을 누른 자리에서 결과를 읽게 하려는 것이다.
// 종전에는 결과가 패널 맨 위 한 칸에만 떠서, 아래 단계에서 실행하면 화면 밖이었다.
// 나란히 두지 않고 갈아끼우는 이유 둘: 결과 문구가 안내의 내용을 이미 품고 있어 겹치면
// 같은 말을 두 번 하고, 안내는 '실행 전에 읽는 것'이라 결과가 온 뒤에는 할 일이 없다.
// 크기·간격·정렬은 안내와 같게 두고 색만 바꾼다 — 위계가 아니라 같은 층위의 상태 변화다.
function StepActions({ hint, message = null, aside, children }) {
  const hasText = !!(aside || message || hint);
  const hasButtons = React.Children.count(children) > 0;
  // 아무것도 없으면 자리를 만들지 않는다 — ④는 안내를 [검수 시작] 위로 올려서
  // 재검수 대기가 0건이면 이 바에 남는 것이 없다. 빈 상자가 8px 틈만 남기는 것을 막는다.
  if (!hasText && !hasButtons) return null;
  return (
    <div className="mt-3 space-y-2">
      {/* 글은 한 줄을 통째로 갖는다 — 옆에 입력칸·버튼을 세우면 세 칸으로 갈려
          긴 문장이 좁은 기둥에 갇히고, 창 폭에 따라 몇 줄이 될지가 매번 달라진다. */}
      {hasText && (
        <div className="space-y-1">
          {aside && <div className="flex flex-wrap items-center gap-2">{aside}</div>}
          {message ? (
            <p className={`text-xs ${message.type === 'error' ? 'text-red-600' : 'text-blue-600'}`}>
              {message.text}
            </p>
          ) : (
            hint && <p className="text-xs text-gray-400">{hint}</p>
          )}
        </div>
      )}
      {hasButtons && <div className="flex flex-wrap items-center justify-end gap-2">{children}</div>}
    </div>
  );
}

// AI 요청 창(AiRunModal)의 안내 끝에 붙는 한 줄 — 「비우면 프로젝트 기본 지시가 쓰인다」.
//
// **걸려 있을 때만 그린다.** 없는데 말하면 그것이 거짓이다. 값이 비면 null 을 돌려 줄 자체를 없앤다.
// 대상은 run·analyze 뿐이다(BE-20260829-reply-fe-0828.md §4-2 5-4 — 그림 재요청·figspec 변환에는
// 기본 지시가 적용되지 않는다). 그래서 공용 AiRunModal 안에 문구를 박지 않고 부르는 쪽에서 넣는다 —
// 박으면 그림 창 둘(FigureEditor/FigureModal · ProblemHubV2/ProblemViewTab)에도 떠서 곧바로 거짓말이 된다.
//
// **입력칸에 미리 채우지 않는다.** 채우면 그 문장이 이 호출의 개별 지시(user_prompt)로 나가
// 서버가 문제 행의 reocr_prompt·analysis_prompt 에 기록한다(§4-2 5-3). 그러면 「기본값을 썼다」와
// 「같은 문장을 손으로 쳤다」가 기록에서 갈리지 않는다.
//
// 얼굴은 StepActions 의 안내(hint)와 같은 값이다 — 같은 층위(보조 설명)에 다른 값을 쓰지 않는다.
const PROMPT_PREVIEW_MAX = 60; // 한 줄에 남는 폭. 전문은 title 로 준다
// variant 'run' — ② 실행(force 아님)은 2026-09-25 부터 지시를 비우면 **그 쪽에 전에 걸어 둔 지시를 유지**한다
// (BE-20260925 §2-3 F10 — 종전엔 지웠다). 기본 지시는 걸린 지시가 없는 쪽에만 쓰인다. 문장이 그 순서를 말한다.
function DefaultPromptNotice({ prompt, variant = 'analyze' }) {
  const v = String(prompt ?? '').trim();
  if (!v) return null;
  const shown = v.length > PROMPT_PREVIEW_MAX ? `${v.slice(0, PROMPT_PREVIEW_MAX)}…` : v;
  return (
    <p className="mt-2 text-xs text-gray-400" title={v}>
      {variant === 'run'
        ? <>지시를 비우고 실행하면 쪽마다 전에 걸어 둔 지시가 그대로 쓰이고, 걸린 지시가 없는 쪽에는 이 프로젝트의 기본 지시가 쓰입니다 — 「{shown}」</>
        : <>지시를 비우고 실행하면 이 프로젝트의 기본 지시가 쓰입니다 — 「{shown}」</>}
    </p>
  );
}

// reviewId·onReviewOpened — 주소로 건너온 검수 대상(`?review=`, 문제 v2 「OCR 등록」 배지)과 그것을 연 뒤 주소에서
// 지우라는 알림. 패널은 주소를 모르고 페이지(pages/OcrHub)가 읽어 넘긴다 — 패널이 프로젝트 번호로 key 를 받는 것과 같은 결.
function ProjectDetailPanel({ projectId, onClose, reviewId = null, onReviewOpened = null }) {
  const { getAccessToken } = useAuth();

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [ocrStatus, setOcrStatus] = useState(null);
  const [ocrUpdatedAt, setOcrUpdatedAt] = useState(null); // 마지막 '성공' 갱신 시각 — 진행률 카드의 살아있음 표시
  const [polling, setPolling] = useState(false);

  // 업로드 대기 항목: { key, file, kind, state: 'ready'|'uploading'|'done'|'error', error }
  const [uploads, setUploads] = useState([]);
  const [uploading, setUploading] = useState(false);
  const uploadSeqRef = useRef(0);
  const uploadsRef = useRef(uploads); // 배치 루프에서 최신 목록 참조(루프 중 제거된 항목 스킵용)
  const fileInputRef = useRef(null);

  useEffect(() => { uploadsRef.current = uploads; }, [uploads]);

  const [running, setRunning] = useState(false);
  const [rangeText, setRangeText] = useState('');
  // ② 실행 대상 파일. '' = 전체 파일(계약의 기본값 — file_id를 빼면 서버가 모든 파일에 적용한다).
  // 값은 file_id의 문자열 표기다. 서버로 보낼 때는 files[]의 원본 값을 되찾아 싣는다 —
  // file_id의 타입이 계약에 없어(정수로 보이나 명시가 없다) 화면이 Number로 바꿔 쓰지 않는다.
  const [runFileKey, setRunFileKey] = useState('');
  // 실행 결과 한 칸 — 패널의 모든 단계가 나눠 쓴다. { type: 'info'|'error', text, step? }
  // step 을 달면 그 단계의 버튼 줄(StepActions)에 뜨고, 없으면 종전대로 패널 맨 위에 뜬다.
  // 버튼을 누른 자리에서 결과를 읽게 하려는 것이다 — 맨 위 한 칸은 아래 단계에서 화면 밖이었다.
  const [actionMsg, setActionMsg] = useState(null);

  const [binding, setBinding] = useState(false);
  const [reconcileReport, setReconcileReport] = useState(null); // bind 응답의 문제 매칭 결과(reconcile_report, 세션 내 보관)
  const [problemsReloadKey, setProblemsReloadKey] = useState(0); // 매칭·분석 후 문제 목록 재조회 트리거

  // AI 분석 (FE-4): 실행 + 진행 폴링(ocr-status 패턴)
  const [analysisStatus, setAnalysisStatus] = useState(null);
  const [analysisUpdatedAt, setAnalysisUpdatedAt] = useState(null); // 마지막 '성공' 갱신 시각 — 분석 진행률 카드의 살아있음 표시
  const [analyzing, setAnalyzing] = useState(false);
  // AI 요청 창 — ② 실행과 ⑤ 분석이 각자 연다. null 이면 닫힘.
  // 종전의 window.confirm 문구는 전부 이 창의 안내·경고로 옮겼다.
  const [runAi, setRunAi] = useState(null);      // { warning } — ② 분리+OCR
  const [analyzeAi, setAnalyzeAi] = useState(null); // { mode } — ⑤ AI 분석
  const [analysisPolling, setAnalysisPolling] = useState(false);
  const analysisBusyRef = useRef(false);
  const analysisFailsRef = useRef(0);
  // ⑤ 분석 지도만 따로 다시 읽게 하는 신호.
  // 진행률 카드는 analysis-status(집계)로 5초마다 갱신되는데, 지도는 문제 전량을 훑어야
  // 그릴 수 있어(analysis_status가 목록에만 있다) problemsReloadKey에 매여 있었고,
  // 그 키는 all_done에서만 올라간다 — 분석이 도는 내내 지도가 얼어 있었다.
  // 집계가 실제로 움직였을 때만 올린다. 5초마다 무조건 훑으면 아무것도 안 바뀐 tick에도
  // 전량 조회가 나간다(문제 100건당 1왕복).
  const [analysisMapKey, setAnalysisMapKey] = useState(0);
  const analysisSigRef = useRef(null);

  // 승격·종료 (FE-5)
  const [promoting, setPromoting] = useState(false);
  const [promoteResult, setPromoteResult] = useState(null); // promote 응답(요약·skipped·failed 패널)
  // 등록 전 라벨 확인 창 — 종전 window.confirm 자리다. 등록을 부르는 유일한 입구이기도 하다.
  const [labelModalOpen, setLabelModalOpen] = useState(false);
  // 종료 창 — 시험지 만들기와 종료를 한 흐름으로 잇는다(되돌릴 수 있는 것을 앞에).
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  // ⑦ 종료 창의 「N건 등록 창 열기」로 ⑥ 등록 창에 왔는가 — 등록이 끝나면 「이제 ⑦ 에서 종료하세요」를 덧붙인다
  const promoteFromCloseRef = useRef(false);
  // [종료 취소](F13) 진행 중
  const [reopening, setReopening] = useState(false);
  // ② 실행 창 — 「이 쪽들에 걸린 지시를 지우고 실행」(F10). 창을 열 때마다 끈다
  const [runClearPrompt, setRunClearPrompt] = useState(false);
  // ③ — 이 화면을 연 동안 실패 목록에서 살아난 쪽(F11, 오너 결정 D4). 서버는 「마지막 매칭 뒤 새로 읽은 쪽」을
  // 알려 주지 않아서 화면이 본 것만 센다(runRules.trackRecoveredPages). 매칭에 성공하면 비운다.
  const recoveredRef = useRef({ seen: new Set(), recovered: new Set() });
  const [recoveredCount, setRecoveredCount] = useState(0);
  useEffect(() => {
    const next = trackRecoveredPages(
      recoveredRef.current,
      ocrStatus?.failed_pages,
      (ocrStatus?.pending ?? 0) + (ocrStatus?.processing ?? 0),
    );
    recoveredRef.current = next;
    setRecoveredCount(next.recovered.size);
  }, [ocrStatus]);
  const [closing, setClosing] = useState(false);

  const pollBusyRef = useRef(false);
  const pollFailsRef = useRef(0); // 연속 실패 카운터 — 3회(≈15초) 실패 시 폴링 중단 + 안내

  // ── 세로 스텝 아코디언 상태 ──
  // openKey: undefined = 현재 단계 따라감(자동 펼침) · null = 전부 접힘 · 'key' = 해당 단계 수동 펼침
  const [openKey, setOpenKey] = useState(undefined);
  const [filesOpen, setFilesOpen] = useState(true); // ② 단계 파일 목록 접이식(기본 펼침 — 업로드 직후 확인)
  const [usageOpen, setUsageOpen] = useState(false); // 하단 LLM 사용량 접이식
  // ── 프로젝트 기본 지시(default_prompt) — 하단 접이식 ──
  // 이 값은 ②분리+OCR · ④검수의 다시 OCR · ⑤AI 분석 세 곳에 걸쳐 걸린다(BE-20260829 §4-2 5-4).
  // 그래서 어느 한 단계(StepRow) 안에 두지 않았다 — 한 단에 넣으면 나머지 두 단에서 그것이
  // 걸려 있는지 알 수 없고, 접힌 단 안에 숨어 「지시를 안 넣었는데 왜 결과가 다르지」가 된다.
  // LLM 사용량과 같은 층 — **단계 밖의 프로젝트 전체 속성** — 이 이 값의 자리다.
  const [promptOpen, setPromptOpen] = useState(false);
  const [promptText, setPromptText] = useState('');
  const [promptSaving, setPromptSaving] = useState(false);
  const [promptMsg, setPromptMsg] = useState(null); // { type: 'info'|'error', text }
  // 파일별로 실제 쪼개진 쪽 번호 — { [file_id]: { text, contiguousFromFirst, count, truncated } | null }
  // 키가 없으면 「아직 모름」이고, 그때는 서버 상태 배지를 그대로 쓴다(지어내지 않는다).
  const [filePages, setFilePages] = useState({});

  // 자동 펼침 앵커: 셋업 구간(업로드→분리→매칭)에서는 현재 단계가 바뀔 때마다 새 단계를 자동 펼침.
  // 매칭 완료(status>=300) 후 검수·분석·승격·종료 루프는 하나의 앵커('finalize')로 묶어, 승격으로
  // 300→400 전이돼도 사용자가 펼쳐 둔 단계(예: 승격 결과·failed 재시도 패널)를 접지 않는다.
  const stepAnchor = useMemo(() => {
    const s = detail?.status ?? 0;
    return s >= 300 ? 'finalize' : deriveCurrentStep(detail, ocrStatus);
  }, [detail, ocrStatus]);
  useEffect(() => { setOpenKey(undefined); }, [stepAnchor]);

  const refreshDetail = useCallback(async () => {
    try {
      const d = await getProjectDetail(projectId, getAccessToken);
      setDetail(d);
      setLoadError(null);
      return d;
    } catch (e) {
      setLoadError(e.message || '프로젝트 정보를 불러오지 못했습니다.');
      // 진행 집계가 이 응답에서 오므로, 실패하면 화면의 건수가 갱신 전 값에 굳는다.
      // loadError는 상세가 아예 없을 때만 렌더되어 실패가 보이지 않는다 — 여기서 말해 준다.
      // (예전 loadStats는 실패 시 stats를 null로 떨어뜨려 '모른다'로 물러섰다. 그 자리를 대신한다)
      setActionMsg({ type: 'error', text: '진행 상황을 갱신하지 못했습니다 — 아래 건수는 갱신 전 값입니다.' });
      return null;
    }
  }, [projectId, getAccessToken]);

  const refreshOcrStatus = useCallback(async () => {
    try {
      const s = await getOcrStatus(projectId, getAccessToken);
      setOcrStatus(s);
      setOcrUpdatedAt(Date.now()); // 실패(catch)에서는 갱신하지 않는다 — 카드의 '3초 전'이 거짓이 되면 안 된다
      return s;
    } catch (e) {
      return null; // 진행률은 부가 정보 — 실패해도 패널 본문은 유지
    }
  }, [projectId, getAccessToken]);

  // ── 기본 지시: 서버 값 → 입력칸 ──
  // 의존성이 문자열 하나라 5초 폴링이 detail 을 새 객체로 갈아끼워도 돌지 않는다 —
  // 값이 실제로 달라질 때만 돈다. 배열·객체를 넣으면 타이핑 중에 글자가 사라진다.
  // 여기서 promptMsg 를 지우지 않는다 — 저장은 refreshDetail 로 serverPrompt 를 바꾸므로
  // 이 effect 가 저장 직후에 반드시 한 번 돈다. 지우면 방금 띄운 「저장했습니다」가 그 자리에서 사라진다.
  // 지우는 자리는 저장을 시작할 때 하나뿐이다(saveDefaultPrompt 첫머리).
  const serverPrompt = typeof detail?.default_prompt === 'string' ? detail.default_prompt : '';
  useEffect(() => { setPromptText(serverPrompt); }, [serverPrompt]);

  // ── 기본 지시 저장·해제 ──
  // 빈 문자열·공백만은 null 로 보낸다 = 해제(BE-20260829-reply-fe-0828.md §4-2 5-4).
  // 응답 data 의 모양이 회신에 없다(상세 전문인지 default_prompt 하나인지 — FE-20260829-reply-followup.md
  // Q11 로 되물어 둔 상태다). 그래서 **반환값에는 아무것도 기대지 않는다** — 성공 판정도 화면 갱신도
  // 저장 뒤 다시 읽은 상세로 한다. 어느 모양이 오든 옳은 길이다.
  //
  // ⚠ 반환값으로 401 을 가르지 않는 이유: ocrApi 의 requestJsonWithStatus 는 성공이면 body.data 를
  // 그대로 돌려주므로 봉투가 {status:'success', data:null} 이면 성공인데도 null 이 온다. 401 표식과
  // 같은 값이라 `d === null → return` 은 **성공을 조용한 무동작으로 삼킨다**(utils/apiUtils.js:5-9 가
  // 이 위험에 이름을 붙여 두었다. 그쪽 AUTH_REDIRECT 심볼로 갈라야 하지만 ocrApi 는 아직 null 이다).
  // 대신 이 자리는 성공/실패를 **예외 유무 + 다시 읽기 성공 여부**로 가른다:
  //   · patchProject 가 던지지 않았다 = 서버가 status:'success' 로 답했다(requestJsonWithStatus 는
  //     success 가 아니면 반드시 throw 한다) 또는 401 로 튕기는 중이다.
  //   · 401 이면 뒤이은 상세 GET 도 401 이라 refreshDetail 이 null 을 돌려준다 —
  //     그때는 「저장했습니다」라고 말하지 않는다.
  const saveDefaultPrompt = async (raw) => {
    if (promptSaving) return;
    const next = String(raw ?? '').trim() || null;
    setPromptSaving(true);
    setPromptMsg(null);
    try {
      await patchProject(projectId, { default_prompt: next }, getAccessToken);
      const d = await refreshDetail(); // 입력칸은 위 effect 가 서버 값으로 맞춘다
      if (!d) {
        // 상세를 다시 읽지 못했다 — 401 로 /signin 으로 튕기는 중이거나 순단이다.
        // 저장이 됐는지 모르는 상태다. 모름을 성공으로 말하지 않는다.
        setPromptMsg({ type: 'error', text: '저장 결과를 확인하지 못했습니다 — 새로고침해 값을 확인해 주세요.' });
        return;
      }
      // 서버가 실제로 무엇을 들고 있는지로 한 번 더 견준다(요청한 값이 아니라). 서버는 strip 후
      // 저장하므로 여기서도 trim 해 견준다. 다르면 성공이라 말할 수 없다 — 다만 「저장 안 됨」이라
      // 단정하지도 않는다(서버 정규화일 수도 있다). 본 것만 말한다.
      // 상세에 키 자체가 없으면 견주지 않는다 — 「해제됨(null)」과 「이 서버는 안 싣는다」가 같은 값이
      // 돼 버려, 저장이 됐는데도 어긋났다고 말하게 된다. 그때는 PATCH 가 준 success 봉투를 근거로 삼는다.
      const hasField = Object.prototype.hasOwnProperty.call(d, 'default_prompt');
      const saved = typeof d.default_prompt === 'string' ? d.default_prompt.trim() : '';
      const wanted = next ?? '';
      if (hasField && saved !== wanted) {
        setPromptMsg({ type: 'error', text: '서버에 남은 기본 지시가 방금 입력한 것과 다릅니다 — 값을 확인하고 다시 저장해 주세요.' });
        return;
      }
      setPromptMsg({ type: 'info', text: next ? '기본 지시를 저장했습니다.' : '기본 지시를 해제했습니다.' });
    } catch (e) {
      // 423 = 종료된 프로젝트. 버튼을 미리 잠가 두므로(closed) 여기까지 오는 것은
      // 다른 창에서 그 사이 종료됐을 때다 — 그때도 무엇 때문인지 말한다.
      setPromptMsg({
        type: 'error',
        text: e.httpStatus === 423
          ? '종료된 프로젝트라 기본 지시를 고칠 수 없습니다.'
          : (e.message || '프로젝트 설정 저장에 실패했습니다.'),
      });
    } finally {
      setPromptSaving(false);
    }
  };

  // 최초 로드: 상세 + 진행률, 미완료 페이지가 있으면 폴링 시작.
  // 진행률 1회 실패(s=null) 시에도 프로젝트 상태가 진행형(분리중/처리중)이면 폴링을 시작해
  // 순단 한 번으로 진행률이 영구 동결되는 것을 막는다(폴링 루프가 자체 중단 조건을 가짐).
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      const d = await refreshDetail();
      const s = d ? await refreshOcrStatus() : null;
      if (!alive) return;
      setLoading(false);
      const active = s
        ? s.pending > 0 || s.processing > 0
        : !!d && (d.status === 150 || d.status === 200);
      if (active) setPolling(true);
    })();
    return () => { alive = false; };
  }, [refreshDetail, refreshOcrStatus]);

  // 진행률 폴링 — pending/processing 소진 시 자동 중단, 연속 3회 실패 시 중단 + 안내
  useEffect(() => {
    if (!polling) return undefined;
    pollFailsRef.current = 0;
    const timer = setInterval(async () => {
      if (pollBusyRef.current) return; // 응답 지연 시 중첩 폴링 방지
      pollBusyRef.current = true;
      try {
        const s = await refreshOcrStatus();
        if (s) {
          pollFailsRef.current = 0;
          await refreshDetail(); // 파일별 extracted/failed 카운트 갱신 — busy 가드 안에서 완료(중첩·역전 방지)
          if (s.pending === 0 && s.processing === 0) setPolling(false);
        } else {
          pollFailsRef.current += 1;
          if (pollFailsRef.current >= 3) {
            setPolling(false);
            setActionMsg({ type: 'error', text: '진행률 조회에 반복 실패해 자동 갱신을 중단했습니다. 패널을 다시 열면 재시도합니다.' });
          }
        }
      } finally {
        pollBusyRef.current = false;
      }
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [polling, refreshOcrStatus, refreshDetail]);

  // ── 업로드 ──
  const handleFilesPicked = (e) => {
    const all = Array.from(e.target.files || []);
    const picked = all.filter((f) => f.type.includes('pdf') || /\.pdf$/i.test(f.name));
    const skipped = all.length - picked.length;
    if (picked.length > 0) {
      setUploads((prev) => [
        ...prev,
        ...picked.map((file) => ({ key: `u${(uploadSeqRef.current += 1)}`, file, kind: 1, state: 'ready', error: null })),
      ]);
    }
    setActionMsg(skipped > 0 ? { type: 'error', text: `PDF가 아닌 파일 ${skipped}개는 제외되었습니다.` } : null);
    e.target.value = ''; // 같은 파일 재선택 허용
  };

  const setUploadKind = (key, kind) =>
    setUploads((prev) => prev.map((u) => (u.key === key ? { ...u, kind } : u)));

  const removeUpload = (key) => setUploads((prev) => prev.filter((u) => u.key !== key));

  const handleUploadAll = async () => {
    if (uploading) return;
    const targets = uploads.filter((u) => u.state === 'ready' || u.state === 'error');
    if (targets.length === 0) return;
    setUploading(true);
    setActionMsg(null);
    let failedCount = 0;
    let aborted = false;
    // 파일마다 종류가 달라 반복 호출(지시서 §2) — 순차 업로드로 서버 부하·에러 추적 단순화
    for (const target of targets) {
      // 배치 진행 중 사용자가 제거한 항목은 건너뜀(스냅샷 아닌 최신 목록 기준)
      const live = uploadsRef.current.find((u) => u.key === target.key);
      if (!live) continue;
      setUploads((prev) => prev.map((u) => (u.key === target.key ? { ...u, state: 'uploading', error: null } : u)));
      try {
        // eslint-disable-next-line no-await-in-loop
        const res = await uploadProjectFile(projectId, live.file, live.kind, getAccessToken);
        if (res === null) {
          // 토큰 부재/401 → /signin 리다이렉트 중. 성공으로 오인하지 않도록 되돌리고 중단.
          setUploads((prev) => prev.map((u) => (u.key === target.key ? { ...u, state: 'ready' } : u)));
          aborted = true;
          break;
        }
        setUploads((prev) => prev.map((u) => (u.key === target.key ? { ...u, state: 'done' } : u)));
      } catch (err) {
        failedCount += 1;
        setUploads((prev) => prev.map((u) => (u.key === target.key ? { ...u, state: 'error', error: err.message } : u)));
      }
    }
    setUploading(false);
    setUploads((prev) => prev.filter((u) => u.state !== 'done')); // 성공분은 파일 목록으로 흡수
    // 부분 실패는 ① 본문(실패 행)에만 있었는데, 성공분이 생기면 현재 단계가 ②로 옮겨가며 ①이 접혀
    // 실패가 화면에서 사라진다 → 접힘과 무관한 공용 액션 영역에 반드시 표면화한다.
    if (failedCount > 0) {
      setActionMsg({
        type: 'error',
        text: `${failedCount}개 파일 업로드에 실패했습니다. ① PDF 업로드 단계에서 오류를 확인하고 재시도하세요.`,
      });
    } else if (aborted) {
      setActionMsg({ type: 'error', text: '인증이 만료되어 업로드를 중단했습니다. 다시 로그인한 뒤 재시도하세요.' });
    }
    refreshDetail();
  };

  // 실행 시점의 파일 목록을 ref로도 들고 있는다. runPages의 의존성에 detail을 넣으면
  // OCR 폴링이 5초마다 detail을 새 객체로 바꿀 때마다 runPages·handleRerunPage가 새로 만들어진다.
  const detailFilesRef = useRef([]);
  useEffect(() => { detailFilesRef.current = Array.isArray(detail?.files) ? detail.files : []; }, [detail]);

  // 파일마다 실제로 쪼개진 page_index를 모은다. { [file_id]: { indexes, truncated } | null }
  // null = 조회 실패 또는 목록이 잘림(2000쪽 상한) — 「모른다」는 뜻이고, 이 값으로 무엇도 단정하지 않는다.
  // 배지 표시(펼쳤을 때)와 실행 전후 비교(runPages)가 함께 쓴다.
  const loadPageIndexes = useCallback(async (list) => {
    const out = {};
    for (const f of (Array.isArray(list) ? list : [])) {
      try {
        const acc = [];
        // 쪽에 걸린 지시(pages[].user_prompt — BE-20260925 F10). 실행 창이 「전에 건 지시」를 보여 주고 지울지 고르게 한다.
        // 옛 서버는 이 키를 주지 않는다 — 그러면 비어 있고, 창은 아무 말도 하지 않는다.
        const prompts = {};
        let page = 1;
        let totalPages = 1;
        let truncated = false;
        do {
          // eslint-disable-next-line no-await-in-loop
          const d = await fetchProjectPages(projectId, { page, perPage: 100, fileId: f.file_id }, getAccessToken);
          acc.push(...d.items.map((it) => it.page_index));
          d.items.forEach((it) => {
            const up = typeof it.user_prompt === 'string' ? it.user_prompt.trim() : '';
            if (up) prompts[it.page_index] = up;
          });
          totalPages = d.totalPages || 1;
          page += 1;
          // 상한에서 끊고 사실대로 알린다 — 조용히 자르면 없는 쪽을 안 읽었다고 말하게 된다
          if (page > FILE_PAGES_MAX_REQUESTS) { truncated = totalPages >= page; break; }
        } while (page <= totalPages);
        out[f.file_id] = { indexes: acc, truncated, prompts };
      } catch (e) {
        out[f.file_id] = null; // 이 파일만 '모름'
      }
    }
    return out;
  }, [projectId, getAccessToken]);

  // ── 처리 실행 (분리 동기 + OCR 적재) ──
  // 범위 실행의 코어 — ②의 [분리 + OCR 실행]과 검수 모달의 [이 페이지 다시 OCR]이 함께 쓴다.
  // 부르는 쪽마다 할 말이 다르므로(전체 실행 / 한 장 재실행) 문구는 만들지 않고 적재 건수만 돌려준다.
  // 반환 { queued, skipped, errors, newPages, targetIds } — newPages는 이번 실행이 새로 쪼갠 page_index 배열(모르면 null).
  // skipped·errors 는 한 장 재실행이 queued 0 의 이유를 가르는 데 쓴다 — 「처리중이라 건너뜀」(skipped)과
  // 「그 파일에서 서버 오류」(errors — 응답 200 이어도 results[].error 로 온다)와 「그 쪽이 없음」(둘 다 0)이 모두 queued 0 이다.
  // 부르는 쪽 둘(②의 실행, 검수 모달의 한 장 재실행)이 각자 문구를 만든다.
  // force(완료된 쪽도 다시 읽기)는 검수 모달 경로만 넘긴다 — 아래 handleRerunPage 주석.
  const runPages = useCallback(async ({ pageRange, fileId, userPrompt, force, clearPrompt } = {}) => {
    const list = detailFilesRef.current;
    // 실행 대상 — file_id를 주면 그 파일 하나, 없으면 전체다(ocrApi.js:242).
    const targets = fileId != null
      ? list.filter((f) => String(f.file_id) === String(fileId))
      : list;
    // ⚠ 재는 것은 대상이 아니라 **언제나 전체 파일**이다. 대상만 재면 서버가 file_id를 무시하고
    // 다른 파일까지 쪼갰을 때 화면이 「새로 쪼갠 쪽 없음」이라고 말한다 — 감시하려던 바로 그 사고를
    // 감시망 밖에 두는 셈이다. 파일 수만큼 왕복이 늘지만(파일당 1~20회), run 자체가 동기 분리로
    // 수십 초 걸리는 호출이라 그 앞뒤에 붙는 비용은 작다.
    const measured = list;
    // 분리는 동기다(ocrApi.js:243). run이 반환된 시점에 쪼개기 결과가 확정돼 있으므로
    // 전후 차집합이 곧 「이번 실행이 무엇을 했는가」다. 서버 집계를 믿지 않고 쪽 번호를 직접 센다.
    const before = await loadPageIndexes(measured);
    const data = await runProject(projectId, { pageRange, fileId, userPrompt, force, clearPrompt }, getAccessToken);
    // runProject 가 requestJsonWithStatus 로 옮겨지면서(서버 400 message 를 살리려고 — ocrApi.js)
    // 401 의 반환값이 throw 에서 null 로 바뀌었다. 그대로 진행하면 queued 0 으로 흘러
    // 「적재 0건」이라는 거짓 결과가 뜬다. 부르는 쪽 둘(②실행·검수 모달의 한 장 재실행)이
    // 모두 catch 를 갖고 있으므로, 종전(unwrap)과 같은 자리에서 던져 사실대로 말하게 한다.
    if (data === null) throw new Error('인증이 만료되어 실행을 중단했습니다. 다시 로그인한 뒤 재시도하세요.');
    const queued = data?.queued_for_ocr ?? 0;
    // 서버는 파일 하나가 실패해도 그 파일만 롤백하고 200 으로 끝낸다 — 오류는 results[].error 에만 있다.
    const skipped = data?.skipped_pages ?? 0;
    const errors = (Array.isArray(data?.results) ? data.results : []).map((r) => r?.error).filter(Boolean);
    const after = await loadPageIndexes(measured);
    // 잴 것이 하나도 없으면(첫 렌더 직후 ref가 아직 비었을 때) diffNewPages는 {total:0}을 돌려주고
    // 화면은 그것을 「새로 쪼갠 쪽이 없습니다」로 단정한다. 모름을 0으로 말하지 않는다 — null이 맞다.
    const newPages = measured.length === 0 ? null : diffNewPages(before, after);
    // 방금 잰 값을 배지에도 반영한다 — 목록이 펼쳐져 있으면 즉시 갱신된다(effect 재조회를 기다리지 않는다)
    const view = toFilePagesView(after);
    setFilePages((prev) => ({ ...prev, ...view }));
    await refreshDetail();
    const s = await refreshOcrStatus();
    // 대기열에 적재됐다면 진행률 조회 1회 실패(s=null)와 무관하게 폴링 시작(루프가 자체 중단 조건 보유)
    if (queued > 0 || (s && (s.pending > 0 || s.processing > 0))) setPolling(true);
    // 서버가 해석한 대상 쪽 수(target_pages) — 대기열 0 의 이유를 가른다(이미 읽어 건너뜀 / 범위에 쪽 없음)
    const targetPageCount = Array.isArray(data?.target_pages) ? data.target_pages.length : null;
    return { queued, skipped, errors, newPages, targetPageCount, targetIds: targets.map((f) => String(f.file_id)) };
  }, [projectId, getAccessToken, refreshDetail, refreshOcrStatus, loadPageIndexes]);

  // ② 실행 창을 연다 — 여기서 범위를 검증하고 경고 문구를 만들어 창에 넘긴다.
  // 종전의 window.confirm 둘(범위 비었을 때·파일 여럿일 때)이 그 경고가 됐다.
  //
  // preset — 실패 쪽 목록의 [다시 읽기](F9)가 넘기는 { pageRange:[i,i], fileId, label, reason }.
  // 범위 칸·파일 선택을 건드리지 않고 이 창에만 대상을 싣는다(handleRun 이 창에 실린 것을 읽는다).
  // ⚠ 버튼 onClick 에 이 함수를 그대로 넘기면 클릭 이벤트가 preset 자리로 들어간다 — 화살표로 감싼다.
  const openRunAi = (preset = null) => {
    if (running) return;
    const pageRange = preset ? preset.pageRange : parsePageRange(rangeText);
    if (pageRange === undefined) {
      setActionMsg({ type: 'error', text: '페이지 범위 형식이 올바르지 않습니다. 쪽 번호는 1부터 셉니다 — 예: 1-30 (비우면 전체)', step: 2 });
      return;
    }
    const fileList = Array.isArray(detail?.files) ? detail.files : [];
    const picked = preset
      ? fileList.find((f) => String(f.file_id) === String(preset.fileId)) || null
      : fileList.find((f) => String(f.file_id) === runFileKey) || null;
    const targetCount = picked ? 1 : fileList.length;
    const failedNow = ocrStatus?.failed ?? detail?.failed_pages ?? 0;
    let warning = null;
    if (preset) {
      warning = preset.reason === 'truncated'
        ? '이 쪽은 출력이 잘려 실패했습니다 — 같은 입력이면 같은 실패가 납니다. 아래 지시로 읽는 방식을 바꿔 보세요'
          + '(예: 「그림 설명은 짧게」·「풀이는 옮기지 말고 본문만」). 이런 쪽은 호출 한 번에 비용이 큽니다.'
        : null;
    } else if (pageRange === null && (detail?.split_pages ?? 0) > 0) {
      // 실패한 쪽도 이 실행에서 다시 읽힌다(서버가 실패·미큐잉 쪽을 다시 대기열에 넣는다 — F8)
      const failedPart = failedNow > 0 ? `읽지 못한 쪽 ${failedNow}개와 ` : '';
      warning = `페이지 범위가 비어 있습니다 — ${picked ? `「${picked.original_filename}」에서` : '이 프로젝트의 모든 파일에서'} ${failedPart}아직 읽지 않은 쪽을 전부 OCR 합니다. `
        + '되돌릴 수 없고, 쪽마다 비용이 듭니다. 일부만 실행하려면 취소하고 범위를 적으세요(예: 1-10).';
    } else if (pageRange !== null && targetCount > 1) {
      const perFile = pageRange[1] - pageRange[0] + 1;
      warning = `페이지 범위가 파일 ${targetCount}개에 각각 적용됩니다 — ${pageRange[0] + 1}-${pageRange[1] + 1}쪽 × 파일 ${targetCount}개 = 최대 ${perFile * targetCount}쪽이 처리됩니다. `
        + '되돌릴 수 없고, 쪽마다 비용이 듭니다. 한 파일만 실행하려면 취소하고 왼쪽에서 파일을 고르세요.';
    }
    // 대상 쪽에 전에 걸어 둔 지시 — 모르면 null(창이 아무 말도 하지 않는다)
    const prompts = promptsForTargets(filePages, fileList, picked ? picked.file_id : null, pageRange);
    setRunClearPrompt(false);
    setRunAi({ warning, preset, prompts });
  };

  // 실행 본체. 확인·경고는 openRunAi 가 창에 넘겼으니 여기서 다시 묻지 않는다.
  const handleRun = async (userPrompt) => {
    if (running) return;
    const req = runAi; // 닫기 전에 창에 실린 대상·선택을 쥔다
    const clearPrompt = runClearPrompt;
    setRunAi(null);
    const pageRange = req?.preset ? req.preset.pageRange : parsePageRange(rangeText);
    if (pageRange === undefined) {
      setActionMsg({ type: 'error', text: '페이지 범위 형식이 올바르지 않습니다. 쪽 번호는 1부터 셉니다 — 예: 1-30 (비우면 전체)', step: 2 });
      return;
    }
    const fileList = Array.isArray(detail?.files) ? detail.files : [];
    // 고른 파일이 목록에서 사라졌으면 전체로 되돌린다 — 5초 폴링이 files[]를 통째로 갈아끼우므로
    // 선택값이 고아가 될 수 있고, 없는 file_id를 보내면 서버가 어떻게 처리하는지 계약에 없다.
    const picked = req?.preset
      ? fileList.find((f) => String(f.file_id) === String(req.preset.fileId)) || null
      : fileList.find((f) => String(f.file_id) === runFileKey) || null;
    const fileId = picked ? picked.file_id : undefined;
    const targetCount = picked ? 1 : fileList.length;

    // 범위가 비면 대상 파일의 아직 읽지 않은 쪽 전부가 대상이다(ocrApi.js:242).
    // 이미 읽은 것이 있는 상태에서 이 실행은 「조금 더」가 아니라 「나머지 전부」다 —
    // 2026-08-23 오너가 한 장만 읽으려다 책 전체를 읽은 사고가 이 자리에서 났고, 취소할 수단이 없다
    // (ocrApi.js 전량에 실행 취소·큐 비우기 엔드포인트가 없다). 그래서 실행 전에 한 번 묻는다.
    // 아직 아무것도 읽지 않았을 때는 묻지 않는다 — 그때의 전체 실행은 예상된 동작이다.
    // 범위를 적었는데 대상이 여러 파일이면 그 범위는 **파일마다 각각** 적용된다(ocrApi.js:242).
    // 1-10을 적은 사람은 10쪽을 생각하지만 파일이 셋이면 30쪽이 나간다. 곱셈 결과를 눌러 보이기 전에
    // 숫자로 말한다 — 실행 뒤에는 되돌릴 수단이 없어 이 확인창이 마지막 자리다.
    setRunning(true);
    setActionMsg(null);
    try {
      const { queued, skipped, newPages, targetIds } = await runPages({
        pageRange, fileId, userPrompt, clearPrompt: clearPrompt && !userPrompt,
      });
      setActionMsg(buildRunMessage({
        pageRange,
        queued,
        skipped,
        newPages,
        targetIds,
        nameOf: (id) => fileList.find((f) => String(f.file_id) === String(id))?.original_filename || `파일 #${id}`,
      }));
    } catch (e) {
      setActionMsg({ type: 'error', text: e.message || '처리 실행에 실패했습니다.', step: 2 });
    } finally {
      setRunning(false);
    }
  };

  // 검수 모달의 [이 페이지 다시 OCR] — 지금 보고 있는 한 장을 **완료된 쪽이어도** 다시 읽는다.
  // 쓰임새는 문제가 통째로 빠진 쪽을 되살리는 것이다(운영 프로젝트 23 — 3쪽이 출력 상한에서 잘려 12번 누락).
  // force 는 이 한 경로에서만 보낸다(BE-20260914-reply-page-reocr-force.md §4-1) — ②의 [분리 + OCR 실행]은
  // 종전대로 완료·처리중을 건너뛴다. 서버는 force 를 「file_id + 한 장 page_range」일 때만 받고(아니면 400)
  // 호출 1회 = 최대 1쪽을 보장한다. 파일을 특정하지 못하면 모달이 버튼을 잠근다(targetFileId).
  // 종전(2026-08-23~09-13)에는 완료된 쪽을 서버가 조용히 건너뛰어 이 버튼이 대개 아무 일도 하지 않았다.
  // 모달이 run의 body 모양(page_range 배열)을 알 필요는 없으므로 여기서 만든다.
  // 결과 문구도 모달이 만든다 — 패널 쪽 안내는 모달에 가려 보이지 않는다.
  //
  // 모달의 page_index(0-base)와 page_range 는 같은 축이다(BE 회신으로 확정 — 0부터·양끝 포함).
  // 그대로 보낸다. [0,0] = 첫 장 하나이고, 과거 「전체 실행」 사고는 서버 split 큐잉 버그였다(수정됨).
  const handleRerunPage = useCallback(
    ({ pageIndex, fileId, userPrompt }) => {
      const idx = pageIndex ?? 0;
      return runPages({ pageRange: [idx, idx], fileId, userPrompt, force: true });
    },
    [runPages],
  );

  // ── 파일별 '실제 읽은 쪽' 조회 ──
  // 파일 목록을 펼쳤을 때만 부른다 — 접혀 있으면 왕복이 0이다(목록은 기본 펼침이지만 접을 수 있다).
  // 의존성을 files 배열로 잡으면 안 된다: OCR 폴링이 5초마다 detail을 새 객체로 바꿔 무한 재조회가 된다.
  // 쪼갠 쪽수(split_pages)가 변할 때만 다시 부르도록 지문(fingerprint) 문자열로 잡는다.
  const filesFingerprint = (Array.isArray(detail?.files) ? detail.files : [])
    .map((f) => `${f.file_id}:${f.split_pages ?? 0}`)
    .join('|');


  useEffect(() => {
    if (!filesOpen || !filesFingerprint) return undefined;
    const list = Array.isArray(detail?.files) ? detail.files : [];
    let alive = true;
    (async () => {
      const raw = await loadPageIndexes(list);
      if (!alive) return;
      setFilePages(toFilePagesView(raw));
    })();
    return () => { alive = false; };
  }, [filesOpen, filesFingerprint, loadPageIndexes]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshAnalysisStatus = useCallback(async () => {
    try {
      const s = await getAnalysisStatus(projectId, getAccessToken);
      setAnalysisStatus(s);
      setAnalysisUpdatedAt(Date.now()); // 실패(catch)에서는 갱신하지 않는다 — 카드의 '3초 전'이 거짓이 되면 안 된다
      return s;
    } catch (e) {
      return null; // 부가 정보 — 실패해도 패널 본문 유지
    }
  }, [projectId, getAccessToken]);

  // 매칭 완료(status>=300) 프로젝트는 분석 현황 1회 조회(재검수 배지) — 진행 중이면 폴링 시작.
  // detail 객체가 아닌 status 값에 의존 — OCR 폴링이 5초마다 detail을 새 객체로 바꿔도 재조회하지 않는다.
  const detailStatus = detail?.status ?? null;
  // in-flight 분석 폴링 tick은 effect 클로저의 stale detail을 참조하므로, 최신 상태를 ref로도 추적.
  // 종료(>=500) 직후 도착하는 all_done 응답이 '종료' 안내를 덮어쓰지 않도록 tick에서 읽는다(FE-6 §2).
  const detailStatusRef = useRef(detailStatus);
  useEffect(() => { detailStatusRef.current = detailStatus; }, [detailStatus]);
  useEffect(() => {
    // 종료(>=500)도 게이트 — 종료 전환 시 effect 재실행으로 폴링이 재시작되는 것 방지(FE-5 수정요청 #1-2)
    if (detailStatus == null || detailStatus < 300 || detailStatus >= 500) return undefined;
    let alive = true;
    refreshAnalysisStatus().then((s) => {
      if (!alive || !s) return;
      if ((s.pending ?? 0) + (s.processing ?? 0) > 0) setAnalysisPolling(true);
    });
    return () => { alive = false; };
  }, [detailStatus, refreshAnalysisStatus]);

  // ── 검수 화면 상태 ──
  // 단계 본문 밖에서 쥔다. StepRow는 펼쳐졌을 때만 본문을 그리므로, ④ 안에 두면 접는 순간
  // 목록이 통째로 사라지고 다른 단계에서 검수를 열 때 ④를 먼저 펼쳐야 한다.
  // 검수는 ④의 소유물이 아니라 여러 단계에서 불리는 횡단 도구다.
  const list = useOcrProblemList({
    projectId,
    reloadKey: problemsReloadKey,
    enabled: (detailStatus ?? 0) >= 300, // 매칭 완료 후에만 문제가 있다
    closed: (detailStatus ?? 0) >= 500,
    // 승인·반려 뒤에는 판정의 근거가 되는 두 숫자를 다시 읽는다. 목록만 새로 읽으면
    // 재검수 대기가 실제로 0이 돼도 화면은 승인 전 숫자를 계속 보고 단계를 넘기지 않는다.
    onMutated: useCallback(async () => {
      await Promise.all([refreshDetail(), refreshAnalysisStatus()]);
      // 인덱스 지도는 reloadKey로만 다시 읽는다 — 삭제·번호 변경이 그 격자를 바꾸므로 함께 올린다.
      // 이게 없으면 지운 충돌이 지도에 그대로 남아, 삭제 확인 문구가 한 약속을 화면이 반증한다.
      setProblemsReloadKey((k) => k + 1);
    }, [refreshDetail, refreshAnalysisStatus]),
  });

  // ── 검수 모달의 승인·반려 ──
  // 목록은 다시 읽지 않고 그 한 줄만 갈아끼운다(patchItem). 표를 다시 읽으면 「로딩 중…」이
  // 표 자리를 차지해 스크롤이 맨 위로 돌아가는데, Enter로 훑는 검수에서는 한 건마다 그랬다.
  // 집계 두 숫자는 그대로 다시 읽는다 — 그건 표가 아니라 진행률 카드와 단계 판정이 쓰는 값이라
  // 낡으면 재검수 대기가 0이 돼도 단계가 넘어가지 않는다.
  // 지도 둘(인덱스·분석)은 프로젝트 전량 조회라 승인 한 건마다 다시 읽을 수 없다. 모달이 지도를
  // 덮고 있는 동안은 낡아도 보이지 않으므로, 낡았다는 표시만 해 두고 모달을 닫을 때 한 번 읽는다.
  const [mapsStale, setMapsStale] = useState(false);
  const { patchItem } = list; // 훅이 돌려주는 객체가 아니라 그 안의 고정 함수에 매단다(매 렌더 새 콜백 방지)
  const handleReviewed = useCallback(async (ocrProbId, rowPatch) => {
    patchItem(ocrProbId, rowPatch);
    setMapsStale(true);
    await Promise.all([refreshDetail(), refreshAnalysisStatus()]);
  }, [patchItem, refreshDetail, refreshAnalysisStatus]);

  // 모달이 닫히는 순간 — 그동안 승인·반려로 낡은 지도와 목록을 한 번에 맞춘다.
  // 닫는 경로가 여럿이라([닫기]·ESC·프로젝트 전환) 버튼이 아니라 열림 상태의 전환을 본다.
  // 검수 모달을 어느 얼굴로 열 것인가 — ④는 OCR 결과만, ⑤는 분석 결과만(오너 결정 2026-08-30).
  // 여는 자리가 모드를 정한다. 모달 안에서 좌측 목록으로 문제를 넘겨도 모드는 유지된다.
  const [reviewMode, setReviewMode] = useState('ocr');
  const openForOcr = useCallback((id, i) => { setReviewMode('ocr'); list.openById(id, i); }, [list]);
  const openForAnalysis = useCallback((id, i) => { setReviewMode('analysis'); list.openById(id, i); }, [list]);
  // 바깥에서 주소로 건너온 검수 대상은 ④ 와 같은 얼굴(OCR 결과)로 연다 — 원본 지면과 그 문제의 자리가 보이는 쪽이다
  useReviewDeepLink({ reviewId, projectId, detailStatus, open: openForOcr, onOpened: onReviewOpened, getAccessToken });
  const reviewOpen = list.selectedProbId != null;
  const prevReviewOpenRef = useRef(reviewOpen);
  useEffect(() => {
    const was = prevReviewOpenRef.current;
    prevReviewOpenRef.current = reviewOpen;
    if (was && !reviewOpen && mapsStale) {
      setMapsStale(false);
      setProblemsReloadKey((k) => k + 1);
    }
  }, [reviewOpen, mapsStale]);

  // 진행 집계 — ④⑤⑥ 판정이 서는 자리. 상세 응답에 실려 오므로 따로 조회하지 않는다.
  const stats = useMemo(() => deriveStats(detail), [detail]);
  const currentStepKey = useMemo(
    () => deriveCurrentStep(detail, ocrStatus, analysisStatus, stats),
    [detail, ocrStatus, analysisStatus, stats],
  );

  // 분석 진행 폴링 — all_done 시 중단 + 목록 새로고침, 연속 3회 실패 시 중단 + 안내
  useEffect(() => {
    if (!analysisPolling) return undefined;
    analysisFailsRef.current = 0;
    const timer = setInterval(async () => {
      if (analysisBusyRef.current) return;
      analysisBusyRef.current = true;
      try {
        const s = await refreshAnalysisStatus();
        if (s) {
          analysisFailsRef.current = 0;
          // 집계가 움직였으면 지도를 다시 읽는다 — 「완료 81 / 134건」이 오르는 동안
          // 그 81이 어느 번호인지도 함께 움직여야 한다. 값이 그대로면 훑지 않는다.
          const sig = `${s.done}/${s.failed}/${s.processing}/${s.pending}`;
          if (analysisSigRef.current !== sig) {
            analysisSigRef.current = sig;
            setAnalysisMapKey((k) => k + 1);
          }
          if (s.all_done) {
            setAnalysisPolling(false);
            // 분석 진행 중 프로젝트가 종료되면(>=500) in-flight all_done 응답이 '프로젝트가 종료되었습니다'
            // 안내를 덮어쓸 수 있어, 종료 상태면 완료 안내만 생략한다(데이터 갱신은 그대로 수행, FE-6 §2).
            if ((detailStatusRef.current ?? 0) < 500) {
              setActionMsg({ type: 'info', text: `AI 분석 완료 — 재검수 대기 ${s.rereview_pending ?? 0}건. 문제 목록에서 확인하세요.` });
            }
            await refreshDetail();
            setProblemsReloadKey((k) => k + 1);
          }
        } else {
          analysisFailsRef.current += 1;
          if (analysisFailsRef.current >= 3) {
            setAnalysisPolling(false);
            setActionMsg({ type: 'error', text: '분석 진행률 조회에 반복 실패해 자동 갱신을 중단했습니다.' });
          }
        }
      } finally {
        analysisBusyRef.current = false;
      }
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [analysisPolling, refreshAnalysisStatus, refreshDetail]);

  // ── AI 분석 실행: 검수 승인된 문제의 개념·난이도 분석 + 정답/풀이 생성(데몬 비동기).
  // 409 = 매칭 전/종료 프로젝트, 404 = 대상 없음(검수 승인된 문제 없음 — 지시서 지정 문구). ──
  // 분석은 LLM을 호출해 비용이 들고, 시작하면 데몬이 가져가 중간에 멈출 수 없다 — 먼저 묻는다.
  // 대상 건수는 프런트가 정확히 셀 수 없다(목록 API에 analysis_status 필터가 없고,
  // 집계 6종으로도 'r200이면서 a∈{100,500}'은 나오지 않는다). 그래서 건수를 지어내지 않고
  // 대상의 정의를 적는다 — 실제 건수는 실행 직후 진행률 카드가 말한다.
  // 분석 실행 안내 — 종전 window.confirm 의 문구를 그대로 옮겼다. 이제 AiRunModal 이 그린다.
  const ANALYZE_SUMMARY = {
    again: '아직 분석하지 않은 문제를 다시 분석합니다. 대상은 검수 승인된 문제 중 미분석·실패한 것입니다. '
      + '이미 분석이 끝난 문제는 대상이 아닙니다 — 특정 문제만 다시 돌리려면 그 문제를 열어 [이 문제 다시 분석]을 쓰세요.',
    first: 'AI 분석을 실행합니다. 대상은 검수 승인된 문제 중 아직 분석하지 않은 것(실패한 것 포함)입니다.',
  };
  const ANALYZE_WARNING = 'LLM 을 호출하므로 비용이 들고, 시작하면 중간에 멈출 수 없습니다. '
    + 'AI 가 정답·풀이를 만든 문제는 검수대기로 되돌아와 한 번 더 확인해야 합니다.';

  // ocrProbIds를 주면 그 문제들만 다시 분석한다 — 지정 호출은 분석 완료(200)여도 다시 돌린다.
  // 주지 않으면 기본 대상(검수 승인 && analysis_status ∈ {미실행, 실패})이다.
  const handleAnalyze = async (ocrProbIds = null, userPrompt = null) => {
    if (analyzing) return;
    const targeted = Array.isArray(ocrProbIds) && ocrProbIds.length > 0;
    setAnalyzing(true);
    setActionMsg(null);
    try {
      const data = await analyzeProject(projectId, getAccessToken, ocrProbIds, userPrompt);
      if (data === null) return; // 토큰 부재/401 → /signin 리다이렉트 중
      const msg = targeted
        ? `${ocrProbIds.length}건을 다시 분석합니다. 진행률을 자동 갱신합니다.`
        : 'AI 분석을 시작했습니다. 진행률을 자동 갱신합니다.';
      setActionMsg({ type: 'info', text: msg });
      await refreshAnalysisStatus(); // 이전 분석의 stale 카운트가 첫 tick(5초)까지 남지 않도록 즉시 갱신
      // 서명을 비워 첫 tick이 반드시 지도를 한 번 읽게 한다 — 방금 대기열에 들어간 문제들이
      // 「분석대기」로 칠해지는 것이 이 실행의 첫 눈에 보이는 결과다.
      analysisSigRef.current = null;
      setAnalysisPolling(true); // all_done 도달 시 폴링이 스스로 중단
      return { ok: true, message: msg };
    } catch (e) {
      // 404 = 분석할 대상이 없음. 기본 호출은 '승인됐고 아직 분석 안 된 문제'가 대상이라
      // 재검수 대기만 남았을 때도 여기로 온다. 지정 호출이면 그 문제가 처리 중이라는 뜻이다.
      let text;
      if (e.httpStatus === 404) {
        text = targeted
          ? '지금은 이 문제가 분석 대상이 아닙니다 — 이미 분석이 처리 중일 수 있습니다.'
          : '분석할 문제가 없습니다 — 승인된 문제 중 아직 분석하지 않은 것이 있어야 합니다. 재검수 대기를 먼저 승인하세요.';
      } else {
        text = e.message || 'AI 분석 실행에 실패했습니다.';
      }
      setActionMsg({ type: 'error', text });
      return { ok: false, message: text }; // 모달에서 부른 경우 그쪽에도 사유를 띄운다
    } finally {
      setAnalyzing(false);
    }
  };

  // ── 문제 등록(최종 컨펌): 검수 완료 문제를 정식 문제로 등록.
  // 화면 문구는 '등록'이지만 API·필드는 promote/promoted 다 — 백엔드 계약이라 식별자는 그대로 둔다. 부분 성공 정상·멱등(재실행 안전).
  //
  // 확인은 PromoteLabelModal 이 한다(종전 window.confirm 을 창이 흡수했다 — 커밋 c71f0b7 과 같은 방향).
  // **이 함수를 부르는 자리는 그 창의 [문제 등록 실행] 하나뿐이어야 한다.** 창 밖에 호출부가 남으면
  // 확인 한 번 없이 되돌릴 수 없는 전체 등록이 실행된다(결과 카드의 [재시도]가 그 자리였다). ──
  const handlePromote = async (ocrProbIds) => {
    if (promoting || closing) return;
    setPromoting(true);
    setActionMsg(null);
    try {
      // 자동 「N번」(인덱스)을 끈다. 그 값은 백엔드도 staging 내부 정렬·충돌 판정용이라 못박았고
      // (docs/ocr-hub-v2/ocr-handoff-replies-0811.md §5.3-1), 지면 번호와 같은 「N번」 꼴이라
      // 문제은행에 함께 실리면 어느 쪽이 교재 번호인지 갈리지 않는다.
      // 번호 라벨은 매칭 직후 굳힌 지면 번호(extra_labels)로만 나간다 — freezeAndReindex 참조.
      // 대상을 창이 좁혀 준다 — AI 분석까지 끝나고 그 결과가 승인된 것만(오너 조건 2026-08-30).
      // 서버 기본 조건은 이보다 느슨해 분석 없이도 올라가므로 ocr_prob_ids 로 못박는다.
      const data = await promoteProject(projectId, getAccessToken, {
        autoLabels: false,
        ocrProbIds: Array.isArray(ocrProbIds) && ocrProbIds.length > 0 ? ocrProbIds : null,
      });
      if (data === null) return; // 토큰 부재/401 → /signin 리다이렉트 중
      setPromoteResult(data);
      const fromClose = promoteFromCloseRef.current;
      promoteFromCloseRef.current = false;
      setActionMsg({
        type: 'info',
        text: `등록 ${data.promoted_now ?? 0}건 완료 (프로젝트 누적 ${data.total_promoted ?? 0}건)`
          + (fromClose ? ' — 이제 ⑦ 에서 종료하면 등록한 문제가 시험지에 담깁니다.' : ''),
      });
      setLabelModalOpen(false); // 성공했으면 창을 닫는다 — 결과(요약·skipped·failed)는 ⑥ 카드가 그린다
      await refreshDetail();
      setProblemsReloadKey((k) => k + 1); // 목록 새로고침 — 현재 페이지 유지(R5 패턴)
    } catch (e) {
      // 실패하면 창을 열어 둔다 — 사유를 보고 라벨을 고쳐 다시 누를 수 있어야 한다
      setActionMsg({ type: 'error', text: e.message || '문제 등록에 실패했습니다.' });
    } finally {
      setPromoting(false);
    }
  };

  // ── 프로젝트 종료 뒤처리. 종료 호출 자체는 ClosePaperModal 이 한다 —
  // 시험지 만들기(되돌릴 수 있다)를 종료(423 으로 잠긴다)보다 먼저 두려면 두 쓰기가 한 흐름에 있어야 한다. ──
  const handleClosed = async (data) => {
    setCloseModalOpen(false);
    setClosing(true);
    try {
      setAnalysisPolling(false); // 종료 후 분석 진행 폴링 중단(FE-5 수정요청 #1-2)
      // detailStatusRef는 effect로 갱신돼 한 렌더 늦다 — refreshDetail 전에 동기로 올려,
      // 이미 떠 있는 in-flight 분석 tick이 종료 안내를 덮어쓰는 창을 닫는다(FE-6 §2 가드 보완).
      detailStatusRef.current = 500;
      // 시험지는 서버가 만들어 응답에 실어 준다(BE-20260830 §4) — 프론트가 만든 것이 아니다.
      const paper = data.paper || null;
      setActionMsg({
        type: 'info',
        text: paper
          ? `프로젝트가 종료되었습니다 (누적 등록 ${data.total_promoted ?? 0}건) · 시험지 「${paper.title}」(#${paper.paper_id}) ${paper.item_count ?? 0}문항을 만들었습니다.`
          : `프로젝트가 종료되었습니다 (누적 등록 ${data.total_promoted ?? 0}건) — 등록된 문제가 없어 시험지는 만들지 않았습니다.`,
      });
      // 종료되면 currentStepKey 가 null 이라 아코디언이 **전부 접힌다** — 방금 만든 결과가 화면에서
      // 사라지지 않게 ⑦을 명시로 열어 둔다(자동 추종에 맡기면 닫힌다).
      setOpenKey('close');
      await refreshDetail(); // status 500 → 읽기 전용 배너 전환
    } finally {
      setClosing(false);
    }
  };

  // ── 종료 취소(F13 — BE-20260925 §2-5). 시험지 없이 닫힌 프로젝트만 되돌린다(서버가 가린다).
  // 되돌린 뒤의 status 는 서버가 정한다(등록 있음 400 · 분석함 350 · 문제 있음 300 …) — 화면은 다시 읽기만 한다. ──
  const handleReopen = async () => {
    if (reopening) return;
    if (!window.confirm(
      '종료를 취소하고 프로젝트를 다시 엽니다.\n'
      + '검수·분석한 문제는 그대로 있고, ⑥ 문제 등록부터 이어서 할 수 있습니다.\n\n'
      + '종료를 취소할까요?',
    )) return;
    setReopening(true);
    setActionMsg(null);
    try {
      const d = await reopenProject(projectId, getAccessToken);
      if (d === null) return; // 401 → /signin 리다이렉트 중
      if (d?.status != null) detailStatusRef.current = d.status;
      await refreshDetail();
      await Promise.all([refreshOcrStatus(), refreshAnalysisStatus()]);
      setProblemsReloadKey((k) => k + 1);
      // 500 과 350·400 은 모두 펼침 앵커가 'finalize' 라 앵커 효과가 다시 돌지 않는다 — 여기서 직접 되돌린다.
      // 결과 문구를 ⑥ 에 싣는다 — 자동 추종이 그 단계를 펼쳐 문구가 보이는 자리에 선다.
      setOpenKey(undefined);
      setActionMsg({
        type: 'info',
        step: 6,
        text: '종료를 취소했습니다 — ⑥ 문제 등록부터 이어서 하세요. 등록한 뒤 ⑦ 에서 다시 종료하면 시험지가 만들어집니다.',
      });
    } catch (e) {
      const text = e.httpStatus === 404
        ? '서버가 아직 종료 취소를 지원하지 않습니다 — 백엔드 운영 배포 뒤에 다시 시도하세요.'
        : (e.message || '종료를 취소하지 못했습니다.');
      setActionMsg({ type: 'error', step: 7, text });
    } finally {
      setReopening(false);
    }
  };

  // 매칭 직후 두 가지를 순서대로 한다 — 묻지 않고 바로 한다.
  //   ① OCR 이 읽은 값(지면 번호·배점·유형·단원·원본 쪽수·원본 파일)을 라벨로 굳힌다
  //   ② 인덱스를 원본 등장 순서(1..N)로 정리한다
  //
  // 순서가 뒤집히면 안 된다. ②가 problem_number 에 들어 있던 '지면에 인쇄된 번호'를 덮는데,
  // 그 값은 되돌릴 곳이 없다(본문 stem 에도 남지 않는다 — 2026-08-28 오너 확인).
  // 그래서 ①이 실패하면 ②를 하지 않는다 — 밀어붙이면 교재 번호가 영구 소실되고,
  // 승격 뒤 문제은행에서 「이 교재 24번」을 찾을 길이 없어진다.
  //
  // ①의 대상은 '번호가 바뀌는 행'이 아니라 전체다. 등록(승격)이 자동 「N번」(인덱스)을 붙이지
  // 않게 바꿨으므로(handlePromote), 문제은행에 실릴 번호 라벨은 여기서 굳힌 것뿐이다.
  // 번호가 안 바뀌는 행까지 굳혀야 그 문제도 교재 번호를 갖는다. 배점·유형은 아예 전량이 대상이다 —
  // 문제은행 상세에 그 두 필드가 없어 라벨로 굳히지 않으면 승격과 함께 버려진다.
  // 승격된 행은 뺀다 — PATCH 가 423 으로 막히고, 이미 문제은행에 나가 라벨도 확정됐다.
  //
  // ②는 지면 번호가 두 벌인 시험지(선택형 1~16 + 서답형 1~5)의 번호 충돌을 없앤다. 한 번
  // 훑어 두면 검수는 1..N 으로 줄 세워진 상태에서 시작한다. 승격분이 있으면 planReindexByOrder
  // 가 스스로 막는다(이미 문제은행에 나간 번호를 흔들지 않는다).
  //
  // firstBind: ① 중 '지면 번호'까지 굳힐지. 첫 매칭에서만 참이다(호출부가 매칭 전 status 로 판정).
  // 프런트는 problem_number 안의 값이 지면 번호인지 인덱스인지 **구별할 수단이 없다** — 둘 다
  // 그냥 정수다. 그래서 '아직 한 번도 정리되지 않았다'가 확실한 첫 매칭에서만 굳힌다.
  // 재매칭에서 굳히면, 이 기능 이전에 만들어져 이미 1..N 으로 정리된 프로젝트의 인덱스가
  // 통째로 가짜 지면 번호가 되어 문제은행까지 실려 간다. 애매하면 안 붙이는 쪽으로 실패한다.
  // 배점·유형은 덮이는 값이 아니라 이 제약을 받지 않는다 — 재매칭에서도 굳힌다.
  const freezeAndReindex = useCallback(async (firstBind) => {
    try {
      const { items } = await fetchAllProjectProblems(projectId, getAccessToken);
      const fz = await freezeOcrLabels(
        items
          .filter((p) => p.promoted !== true)
          .map((p) => ({ id: p.ocr_prob_id, printed: p.problem_number, scope: p.scope })),
        getAccessToken,
        (done, total) => {
          setActionMsg({ type: 'info', step: 3, text: `OCR 값을 라벨로 남기는 중… ${done}/${total}` });
        },
        { withPrintedNumber: firstBind },
      );
      if (fz.aborted) return { applied: 0, frozen: 0 }; // 토큰 부재/401 → /signin 리다이렉트 중
      // 굳히기 실패는 재배치 실패와 다음 수가 다르다. 수동 '인덱스 다시 매기기'는 굳히기를
      // 거치지 않으므로, 여기서 그 길로 안내하면 지면 번호를 손으로 지우게 만든다.
      if (fz.failed) {
        return {
          error: `OCR 값을 라벨로 남기지 못해 인덱스를 건드리지 않았습니다 — ${fz.failed} `
            + '매칭을 다시 실행하면 이 단계부터 다시 시도합니다. '
            + "(지금 '인덱스 다시 매기기'를 쓰면 지면 번호가 사라집니다)",
        };
      }
      const r = planReindexByOrder(items);
      if (r.blocked || r.changed.length === 0) return { ...r, frozen: fz.added };
      const steps = planApplySteps(items, r.changed);
      const res = await applyNumberSteps(steps, getAccessToken, (done, total) => {
        setActionMsg({ type: 'info', step: 3, text: `인덱스를 원본 순서대로 정리하는 중… ${done}/${total}` });
      });
      return { ...r, applied: res.aborted ? 0 : r.changed.length, frozen: fz.added };
    } catch (e) {
      // 여기까지 왔으면 굳히기는 끝났다 — 수동 재시도가 지면 번호를 지우지 않는다
      return {
        error: `인덱스 정리에 실패했습니다 — ${e.message || ''} 문제 목록의 '인덱스 다시 매기기'에서 다시 시도하세요.`,
      };
    }
  }, [projectId, getAccessToken]);

  // ── 매칭(제본): 페이지들을 문제 단위로 구성 + 문제 매칭 결과 생성. 재실행 안전(검수 완료분 보존).
  // 409 = 실패 페이지 존재 → 확인 후 exclude_failed로 재호출(지시서 §2). 423 등은 메시지 표면화. ──
  const handleBind = async () => {
    if (binding) return;
    // 매칭 '전'의 status 로 첫 매칭인지 정한다 — 검수 단계(300)에 닿은 적이 있으면 그 프로젝트의
    // problem_number 는 이미 인덱스로 정리됐을 수 있다. bindProject 뒤에 읽으면 늦는다.
    const firstBind = Number(detail?.status ?? 0) < 300;
    setBinding(true);
    setActionMsg(null);
    try {
      let data;
      try {
        data = await bindProject(projectId, {}, getAccessToken);
      } catch (e) {
        if (e.httpStatus === 409) {
          const ok = window.confirm(`${e.message}\n\n실패한 페이지를 제외하고 매칭을 진행할까요?`);
          if (!ok) {
            // 빼지 않기로 했다 — 그 쪽을 다시 읽을 자리(② 의 실패 목록)로 데려간다(F9)
            const listed = Array.isArray(e.data?.failed_pages) && e.data.failed_pages.length > 0;
            setOpenKey('run');
            setActionMsg({
              type: 'info',
              step: 2,
              text: listed
                ? '읽지 못한 쪽을 아래 목록에서 다시 읽은 뒤 ③ 매칭을 하세요.'
                : '읽지 못한 쪽을 다시 읽은 뒤 ③ 매칭을 하세요.',
            });
            return;
          }
          data = await bindProject(projectId, { excludeFailed: true }, getAccessToken);
        } else {
          throw e;
        }
      }
      if (!data) return; // 토큰 부재/401 → /signin 리다이렉트 중
      const report = data.reconcile_report || null;
      setReconcileReport(report);
      const totals = Object.values(report?.scopes || {}).reduce(
        (acc, s) => ({
          body: acc.body + (s.body_count || 0),
          answer: acc.answer + (s.answer_count || 0),
          solution: acc.solution + (s.solution_count || 0),
        }),
        { body: 0, answer: 0, solution: 0 },
      );
      // 새로 읽힌 본문이 검수완료·등록 행과 번호가 겹치면 서버가 별도 검수대기 행을 세운다(B18 — 종전엔 버렸다)
      const newRows = (Array.isArray(data.skipped_new_bodies) ? data.skipped_new_bodies : []).filter((x) => x && x.new_row).length;
      const newRowText = newRows > 0
        ? ` · 새 검수대기 행 ${newRows}개가 생겼습니다(이미 검수한 문제와 번호가 겹쳐 따로 세웠습니다 — ④에서 번호를 확인하세요)`
        : '';
      // 이 매칭이 살아난 쪽을 문제로 올렸다 — ③ 의 「다시 매칭 필요」를 비운다
      recoveredRef.current = { seen: recoveredRef.current.seen, recovered: new Set() };
      setRecoveredCount(0);
      const base = `매칭 완료 — 본문 ${totals.body} · 정답 ${totals.answer} · 풀이 ${totals.solution}${newRowText}`;
      setActionMsg({ type: 'info', step: 3, text: `${base}. OCR 값을 라벨로 남기고 인덱스를 정리하는 중…` });
      const rx = await freezeAndReindex(firstBind);
      let tail;
      if (rx?.error) tail = ` · ${rx.error}`;
      else {
        // 무엇을 굳혔는지 갈래별로 말한다 — 조용히 넘어가면 라벨이 늘어난 이유를, 재매칭에서는
        // 번호 라벨이 없는 이유를 검수자가 알 수 없다
        let frozen = '';
        if (rx?.frozen > 0) {
          frozen = firstBind
            ? ` · OCR 라벨 ${rx.frozen}건을 남겼습니다(지면 번호 · 배점 · 유형 · 단원 · 쪽수 · 파일)`
            : ` · OCR 라벨 ${rx.frozen}건을 남겼습니다(배점 · 유형 · 단원 · 쪽수 · 파일 — 재매칭이라 지면 번호는 건드리지 않았습니다. 새로 들어온 문제는 라벨에 번호를 직접 넣으세요)`;
        }
        if (rx?.blocked === 'promoted') tail = `${frozen} · 등록된 문제 ${rx.promoted.length}건이 있어 인덱스는 건드리지 않았습니다.`;
        else if (rx?.applied > 0) tail = `${frozen} · 인덱스 ${rx.applied}건을 원본 순서대로 정리했습니다.`;
        else tail = `${frozen} · 인덱스는 이미 원본 순서대로였습니다.`;
      }
      setActionMsg({
        type: rx?.error ? 'error' : 'info',
        step: 3,
        text: `${base}${tail} 문제 목록 탭에서 검수 대상을 확인하세요.`,
      });
      await refreshDetail();
      setProblemsReloadKey((k) => k + 1);
    } catch (e) {
      setActionMsg({ type: 'error', step: 3, text: e.message || '매칭 실행에 실패했습니다.' });
    } finally {
      setBinding(false);
    }
  };

  // ── 렌더 ──
  if (loading) {
    return (
      <div className="flex h-full w-full flex-col overflow-hidden rounded-3xl bg-white shadow-lg">
        <div className="flex flex-1 items-center justify-center text-sm text-gray-500">로딩 중...</div>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="flex h-full w-full flex-col overflow-hidden rounded-3xl bg-white shadow-lg">
        <div className="shrink-0 border-b px-4 py-3">
          <div className="flex items-center gap-3">
            <span className="min-w-0 flex-1 truncate text-sm font-bold text-gray-900">프로젝트 #{projectId}</span>
            <PanelCloseButton onClose={onClose} />
          </div>
        </div>
        <div className="flex min-h-0 flex-1 items-center justify-center">
          <EmptyState message="프로젝트를 불러올 수 없습니다" description={loadError || undefined} />
        </div>
      </div>
    );
  }

  const statusInfo = getProjectStatusDisplay(detail.status, detail.status_label);
  const files = Array.isArray(detail.files) ? detail.files : [];
  const locked = (detail.status ?? 0) >= 400; // 컨펌대기/종료 — 업로드·실행·매칭 423
  // 종료(500)만 전면 차단 — 400(컨펌대기)에서는 검수·분석·재승격 계속 가능(FE-5 §3)
  const closed = (detail.status ?? 0) >= 500;
  // 등록 없이 종료됨(prod #35 모양) — 검수·분석한 것이 있는데 등록 0건으로 닫혔다. 정상 종료처럼 읽히지 않게
  // ⑥ 을 경고색으로, 배너·⑦ 에 사실을 적는다(BE-20260925 §3 「#35 화면」). 집계를 모르는 옛 서버에서는 세우지 않는다.
  const unregisteredReviewed = stats ? (stats.reviewDone ?? 0) + (stats.rereviewPending ?? 0) : 0;
  const closedUnregistered = closed && !!stats && (stats.promoted ?? 0) === 0 && unregisteredReviewed > 0;
  const ocrTotal = ocrStatus?.total_pages ?? detail.total_pages ?? 0;
  const ocrDone = ocrStatus?.done ?? detail.extracted_pages ?? 0;
  const ocrFailed = ocrStatus?.failed ?? detail.failed_pages ?? 0;
  // 대기·처리 중 개수는 ocr-status에만 있다 — 상세(detail)로는 대체할 수 없으므로 조회 전에는 null(지어내지 않는다)
  const ocrWaiting = ocrStatus ? ocrStatus.pending ?? 0 : null;
  const ocrProcessing = ocrStatus ? ocrStatus.processing ?? 0 : null;
  const bindAvailable = !!ocrStatus?.bind_available;
  // 문제 매칭 결과: 이 세션의 bind 응답 우선, 없으면 상세 응답에 실려오는 경우 대비(방어적)
  const reconcile = reconcileReport || detail.reconcile_report || null;

  // ── 스텝 파생값 ──
  const S = detail.status ?? 0;
  const hasFiles = files.length > 0 || (detail.file_count ?? 0) > 0;
  const bound = S >= 300; // 매칭 완료 — 검수·분석·승격 루프 진입
  // 집계가 있으면 실제 승격 건수를, 없으면 status로 근사한다(350→400 전이 = 최초 승격 시점)
  const promoted = stats ? stats.promoted > 0 : S >= 400;
  // 대기/처리 중 페이지가 있으면 매칭 완료 이후라도 미완료 — 파일 추가 후 재-OCR 구간에서 ②·③이 '완료'로 굳는 것 방지
  const ocrPending = (ocrStatus?.pending ?? 0) + (ocrStatus?.processing ?? 0);
  const ocrComplete = ocrPending === 0 && (bound || bindAvailable); // OCR 전체 완료(매칭 가능) 이후
  const ocrRunning = !ocrComplete && (S === 150 || S === 200 || polling || ocrPending > 0);
  const rereview = analysisStatus?.rereview_pending ?? 0;
  const totalPromoted = stats?.promoted ?? promoteResult?.total_promoted ?? 0;
  const uploadErrors = uploads.filter((u) => u.state === 'error').length;
  const fileErrors = files.filter((f) => f.error_msg).length;
  // ⑤ 분석 완료 판정: all_done은 '진행 중 작업 없음'(폴링 중단 신호)일 뿐 성공이 아니다.
  // 한 건도 처리 못 했거나 실패가 남아 있으면 완료(초록 체크)로 표시하지 않는다.
  const analysisDone =
    !!analysisStatus?.all_done && (analysisStatus.done ?? 0) > 0 && (analysisStatus.failed ?? 0) === 0;
  // 분석을 한 번이라도 돌렸는가 — 완료 여부와 별개다. 돌린 뒤에는 'AI 결과 확인'이 주 행동이 된다.
  const analysisRan = (analysisStatus?.done ?? 0) > 0 || (analysisStatus?.failed ?? 0) > 0;
  const reconcileTotals = reconcile
    ? Object.values(reconcile.scopes || {}).reduce(
        (a, s) => ({
          body: a.body + (s.body_count || 0),
          answer: a.answer + (s.answer_count || 0),
          solution: a.solution + (s.solution_count || 0),
        }),
        { body: 0, answer: 0, solution: 0 },
      )
    : null;

  // 각 단계 상태: 'done' | 'current' | 'available' | 'locked'
  const stepStateOf = (key) => {
    // 종료 — 전 단계 읽기 전용 완료. 단 등록 없이 닫혔으면 ⑥ 은 끝난 것이 아니다(초록 체크가 정상 종료로 읽힌다)
    if (closed) return key === 'promote' && closedUnregistered ? 'warn' : 'done';
    switch (key) {
      case 'upload':
        return hasFiles ? 'done' : 'current';
      case 'run':
        if (ocrComplete) return 'done';
        if (!hasFiles) return 'locked';
        return currentStepKey === 'run' ? 'current' : 'available';
      case 'bind':
        // 재-OCR이 도는 동안에는 '완료'로 굳히지 않고 재매칭 가능 상태로 표시
        // 이 화면에서 새로 읽은 쪽이 있으면 아직 끝나지 않았다 — 다시 매칭해야 그 쪽 문제가 생긴다(F11)
        if (bound) return ocrComplete && !(recoveredCount > 0 && !locked) ? 'done' : 'available';
        if (!bindAvailable) return 'locked';
        return 'current';
      case 'review':
        if (!bound) return 'locked';
        if (currentStepKey === 'review') return 'current';
        // 원본 미검수가 0일 때만 끝난 것이다. 재검수 대기는 여기서 세지 않는다 —
        // AI가 되돌려 보낸 것이라 ⑤의 일이고, 섞어 세면 ④가 끝나고도 끝나지 않는다.
        if (stats) return stats.reviewPending === 0 ? 'done' : 'available';
        // 폴백: 통계를 모를 때. 분석이 돌았거나 승격했으면 ④를 지난 것으로 본다(근사).
        return analysisRan || promoted ? 'done' : 'available';
      case 'analyze':
        if (!bound) return 'locked';
        // deriveCurrentStep이 이 단계를 지목할 수 있게 됐으므로 여기서도 'current'를 낼 수 있어야 한다.
        // 이게 없으면 판정만 바뀌고 배지는 그대로 ④에 남는다.
        if (currentStepKey === 'analyze') return 'current';
        // 사람이 확인할 재검수분이 남아 있으면 완료가 아니다 — 분석 자체는 끝났더라도.
        if (stats && stats.rereviewPending > 0) return 'available';
        return analysisDone ? 'done' : 'available';
      case 'promote':
        if (!bound) return 'locked';
        if (currentStepKey === 'promote') return 'current';
        if (stats) {
          // 승격은 '남은 일이 없을 때' 끝난다. 검수완료분이 전부 승격됐어도 미검수가 남아 있으면
          // 아직 할 일이 있다 — status 400(컨펌대기)을 완료로 읽던 것이 여기서 어긋났다.
          const nothingLeft = stats.reviewPending === 0 && stats.rereviewPending === 0
            && stats.promoted > 0 && stats.reviewDone <= stats.promoted;
          return nothingLeft ? 'done' : 'available';
        }
        return promoted ? 'done' : 'available';
      case 'close':
        if (!bound) return 'locked';
        return currentStepKey === 'close' ? 'current' : 'available';
      default:
        return 'locked';
    }
  };
  const st = Object.fromEntries(STEP_KEYS.map((k) => [k, stepStateOf(k)]));

  // ── 지나간 단계 잠금 (2026-08-23 오너 결정) ──
  // 원래 방침은 아래 '강조 예산' 주석대로 「완료된 단계의 액션도 막지 않는다」였다. 그 근거가
  // 「API가 재실행 안전」이었는데, ②에서 그 전제가 깨졌다 — 모든 파일을 이미 읽은 뒤의 [다시 실행]은
  // 서버가 통째로 건너뛰어 아무 일도 일어나지 않는다(ocrApi.js:242 · 2026-08-23 오너 확인).
  // 눌러도 아무 일이 없는 버튼은 고장과 구분되지 않는다. 그래서 '지나갔으면'이 아니라
  // **'할 일이 없으면'** 잠그고, 잠긴 이유를 그 단계가 이미 쓰는 자리(안내줄·툴팁)로 말한다.
  // 새 PDF를 올리면 ②③은 저절로 다시 열린다 — 분리되지 않은 파일이 생기기 때문이다.
  //
  // ② 실행 대상 — 고른 파일이 목록에 없으면(폴링이 갈아끼웠다) 전체로 되돌려 읽는다.
  const runPickedFile = files.find((f) => String(f.file_id) === runFileKey) || null;
  const runTargetCount = runPickedFile ? 1 : files.length;
  // 범위 칸의 현재 값. 배열이면 유효한 범위, null이면 비어 있음, undefined면 형식 오류다.
  const runRange = parsePageRange(rangeText);

  // ① 업로드: 매칭을 지나면 닫는다. 그 뒤의 파일 추가는 파이프라인을 되감는 일이다.
  const uploadClosed = bound;
  // ② 다시 실행: 쪼개지 않은 파일이 없고 OCR도 끝났으면 「전체 실행」은 할 일이 없다(status 200 = 분리완료).
  //
  // ⚠ 잠그는 것은 **범위 없는 전체 실행뿐**이다. 범위를 적었거나 파일을 골랐으면 잠그지 않는다.
  // 서버가 파일의 원본 쪽수를 주지 않아(→ 백엔드 §5.2) 「300쪽 중 1-10만 쪼갠 파일」과 「10쪽을 다
  // 쪼갠 파일」이 화면에서 똑같이 status 200 · 「10쪽」으로 보인다. 그 둘을 구별하지 못하면서
  // 「모두 읽었다」고 단정하면 11-20을 읽으려는 사람의 길을 막는다 — 이 화면의 파일 배지가 바로 옆에서
  // 「일부 1–10쪽」이라고 말하고 있는데 버튼은 「다 읽었다」고 말하는 모순이기도 하다.
  //
  // ⚠ 실패 쪽(500)·미큐잉 쪽(105)이 있으면 할 일이 있다 — 서버는 범위 없는 실행에서 그 쪽들을 다시 대기열에 넣는다
  // (api/v2/ocr.py run: 완료·처리중만 건너뜀). 종전엔 이 둘을 보지 않아 prod #46 의 실패 18쪽에서도
  // 버튼이 잠기고 툴팁이 「아무 일도 일어나지 않습니다」라고 거짓을 말했다(BE-20260925 F8).
  const runNothingToDo = hasFiles && ocrComplete && !files.some((f) => f.status !== 200)
    && ocrFailed === 0 && !((ocrStatus?.not_queued ?? 0) > 0)
    && !rangeText.trim() && !runPickedFile;
  // ⑥ 등록 창의 경고 — 첫 등록(컨펌대기 400 전이)이 ②·③ 을 잠그기 전에, 아직 문제로 못 올린 쪽이 남았음을 말한다
  const lockParts = [
    ocrFailed > 0 ? `읽지 못한 쪽 ${ocrFailed}개` : null,
    ocrPending > 0 ? `읽는 중인 쪽 ${ocrPending}개` : null,
    recoveredCount > 0 ? `새로 읽었지만 매칭하지 않은 쪽 ${recoveredCount}개` : null,
  ].filter(Boolean);
  const firstRegisterLockWarning = !locked && lockParts.length > 0
    ? `처음 등록하면 ②·③이 잠겨 쪽을 다시 읽거나 다시 매칭할 수 없습니다. 아직 남은 것: ${lockParts.join(' · ')}. `
      + '그 쪽의 문제까지 등록하려면 먼저 ②에서 다시 읽고 ③ 매칭을 하세요.'
    : null;
  // 범위 × 파일 수가 1을 넘으면 누르기 전에 그 곱셈을 보여 준다. 확인창은 마지막 방어선이고
  // 이 배지는 그 앞이다 — 사람이 「10쪽」이라고 생각하는 동안 화면이 「30쪽」이라고 말한다.
  const runFanout = Array.isArray(runRange) && runTargetCount > 1
    ? (runRange[1] - runRange[0] + 1) * runTargetCount
    : null;
  // ⚠ StepActions는 aside의 truthy 여부로 글줄을 그릴지 정한다. JSX 조각은 내용이 비어도 truthy라
  // 항상 넘기면 빈 줄이 생긴다 — 할 말이 없으면 null을 넘긴다.
  // amber는 「사람이 결정할 일이 남았다」의 색이다(④⑤ 재검수 배지와 같은 얼굴). 확정된 실패가
  // 아니라 이대로 누르면 이만큼 나간다는 예고이므로 red가 아니다.
  const runAside = (locked || runFanout != null) ? (
    <>
      {locked && <span className="text-xs text-gray-400">컨펌 대기/종료 상태에서는 실행할 수 없습니다</span>}
      {runFanout != null && (
        <span className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700">
          범위가 파일 {runTargetCount}개에 각각 적용됩니다 — 최대 {runFanout}쪽
        </span>
      )}
    </>
  ) : null;
  // 강조 예산 — 파란 주 버튼은 '지금 할 일' 배지가 붙은 한 단계에만 준다.
  // 완료된 단계의 액션을 막지는 않는다: 파일 추가→재-OCR→재매칭, 검수를 더 한 뒤 추가 승격이 모두 정당한 경로이고
  // API도 재실행 안전(bind=검수 완료분 보존 / promote=멱등)이라고 명시한다. 대신 테두리 버튼으로 무게를 낮춰
  // "지금 눌러야 하는 것"과 "다시 눌러도 되는 것"을 눈으로 가른다. 비가역인 ⑦ 종료만 closed로 실제 차단된다.
  // ⑤ 분석·⑥ 승격은 deriveCurrentStep이 지목하지 않는 선택 단계라 항상 테두리 버튼이다(의도).
  const stepBtnVariant = (key) => (st[key] === 'current' ? 'primary' : 'primary-outline');
  // 자동 추종(openKey===undefined) 중이라도 미해결 업로드 실패가 있으면 ①을 계속 펼쳐 둔다 —
  // 성공분 때문에 현재 단계가 ②로 넘어가며 실패 행이 접혀 사라지는 것을 막는다(수동 토글은 그대로 우선).
  // 같은 이유로 방금 실행한 단계의 결과(actionMsg.step)가 있으면 그 단계를 계속 펼쳐 둔다.
  // 결과는 그 단계 안내(StepActions.hint) 자리에 뜨므로 단계가 접히면 읽을 자리 자체가 없어진다 —
  // 매칭은 성공하면 status 가 200→300 이 되어 stepAnchor 가 'finalize' 로 바뀌고, 그 리셋이
  // 자동 추종을 되살려 현재 단계를 ④ 검수로 옮긴다. 그대로 두면 ③에 붙인 결과가 그 자리에서 접힌다.
  const msgStepKey = actionMsg?.step != null ? STEP_KEY[actionMsg.step] : null;
  // 등록 없이 종료된 프로젝트는 ⑦ 을 펼쳐 둔다 — 그 사실과 [종료 취소]가 ⑦ 에만 있다(위 안내 배너를 걷어서, 2026-09-25)
  const effectiveOpen = openKey === undefined
    ? (uploadErrors > 0 ? 'upload' : (msgStepKey || currentStepKey || (closedUnregistered ? 'close' : null)))
    : openKey;
  const isOpen = (key) => effectiveOpen === key;
  // 지금 펼쳐진 것(effectiveOpen)을 기준으로 토글 — 자동 추종 중에도 첫 클릭이 헛돌지 않는다
  const toggleStep = (key) => setOpenKey(effectiveOpen === key ? null : key);


  return (
    <div className="flex h-full w-full flex-col overflow-hidden rounded-3xl bg-white shadow-lg">
      {/* 헤더 — 학생·시험지 상세와 같이 오른쪽 끝에 닫기를 둔다. */}
      <div className="shrink-0 border-b px-4 py-3">
        <div className="flex items-center gap-3">
          <span className="min-w-0 flex-1 truncate text-sm font-bold text-gray-900">{detail.name || `프로젝트 #${projectId}`}</span>
          <span className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusInfo.cls}`}>
            {statusInfo.text}
          </span>
          {polling && <span className="shrink-0 text-xs text-gray-400">진행률 갱신 중…</span>}
          <PanelCloseButton onClose={onClose} />
        </div>
      </div>

      {/* 액션 결과 — 단계에 매이지 않은 것만 여기 뜬다(단계에 매인 것은 그 단계의 버튼 줄로 간다) */}
      {actionMsg && actionMsg.step == null && (
        <div className={`shrink-0 px-4 pt-3 text-sm ${actionMsg.type === 'error' ? 'text-red-600' : 'text-blue-600'}`}>
          {actionMsg.text}
        </div>
      )}

      <div className="flex-1 overflow-y-auto overflow-x-hidden p-4">
        {/* 종료 안내 배너는 두지 않는다(오너 2026-09-25) — 같은 사실을 ⑦ 이 말한다:
            시험지 표 한 줄(result_paper_id)·읽기 전용 안내·등록 없이 종료된 사실과 [종료 취소]. */}

        {/* 세로 스텝 카드 — 위→아래 파이프라인. 현재 단계 자동 펼침, 지난 단계는 클릭해 상세보기 */}
        <div className="divide-y divide-gray-100 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">

          {/* ① PDF 업로드 */}
          <StepRow
            n={1}
            title="PDF 업로드"
            state={st.upload}
            summary={
              hasFiles
                ? `${detail.file_count ?? files.length}개 업로드됨${uploadErrors > 0 ? ` · 실패 ${uploadErrors}` : ''}`
                : 'PDF를 추가하세요'
            }
            expandable={st.upload !== 'locked'}
            open={isOpen('upload')}
            onToggle={() => toggleStep('upload')}
          >
            {/* 이미 업로드된 파일 — 업로드 단계에서 바로 확인 */}
            {files.length > 0 && (
              <div className="mb-3 space-y-1">
                <p className="text-xs font-medium text-gray-500">업로드된 파일 {detail.file_count ?? files.length}개</p>
                {files.map((f) => {
                  const kindInfo = getFileKindDisplay(f.kind, f.kind_label);
                  return (
                    <div key={f.file_id} className="flex items-center gap-2 rounded-lg bg-gray-50 px-3 py-1.5">
                      <span className="min-w-0 flex-1 truncate text-sm text-gray-800" title={f.original_filename}>
                        {f.original_filename}
                      </span>
                      <span className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium ${kindInfo.cls}`}>
                        {kindInfo.text}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,application/pdf"
              multiple
              onChange={handleFilesPicked}
              className="hidden"
            />
            {uploads.length > 0 && (
              <div className="space-y-2">
                {uploads.map((u) => (
                  <div key={u.key} className="flex items-center gap-2 rounded-xl border px-3 py-2">
                    <span className="min-w-0 flex-1 truncate text-sm text-gray-900" title={u.file.name}>
                      {u.file.name}
                      <span className="ml-1 text-xs text-gray-400">({(u.file.size / 1024 / 1024).toFixed(1)}MB)</span>
                    </span>
                    <select
                      value={u.kind}
                      onChange={(e) => setUploadKind(u.key, Number(e.target.value))}
                      disabled={u.state === 'uploading' || uploading}
                      className="rounded border px-2 py-1 text-xs"
                    >
                      {Object.entries(OCR_FILE_KIND).map(([k, v]) => (
                        <option key={k} value={k}>{v.label}</option>
                      ))}
                    </select>
                    {u.state === 'uploading' && <span className="shrink-0 text-xs text-blue-500">업로드 중…</span>}
                    {u.state === 'error' && (
                      <span className="max-w-[180px] shrink-0 truncate text-xs text-red-500" title={u.error}>
                        {u.error}
                      </span>
                    )}
                    <button
                      onClick={() => removeUpload(u.key)}
                      disabled={u.state === 'uploading'}
                      className="shrink-0 text-gray-400 hover:text-gray-600 disabled:opacity-40"
                      aria-label="업로드 목록에서 제거"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <StepActions
              hint="시험지·정답·풀이 PDF를 종류별로 지정해 업로드합니다. 파일별 분리·OCR 현황은 ② 단계에서 확인합니다."
              aside={locked
                ? <span className="text-xs text-gray-400">컨펌 대기/종료 상태에서는 추가할 수 없습니다</span>
                : uploadClosed
                  ? <span className="text-xs text-gray-400">매칭을 마친 뒤에는 파일을 추가할 수 없습니다.</span>
                  : null}
            >
              <Button
                // 고른 파일이 있으면 그때의 주 액션은 '업로드'다 — 파일 선택은 뒤로 물린다
                variant={uploads.length === 0 ? stepBtnVariant('upload') : 'primary-outline'}
                size="sm"
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                disabled={uploading || locked || uploadClosed}
                icon={<Upload className="size-4" />}
              >
                파일 선택
              </Button>
              {uploads.length > 0 && (
                <Button
                  size="sm"
                  onClick={handleUploadAll}
                  disabled={uploading || locked || uploadClosed}
                  loading={uploading}
                >
                  업로드 ({uploads.filter((u) => u.state !== 'done').length})
                </Button>
              )}
            </StepActions>
          </StepRow>

          {/* ② 분리 + OCR */}
          <StepRow
            n={2}
            title="분리 + OCR"
            state={st.run}
            summary={
              // 실패(OCR 페이지·파일 오류)는 접힌 상태에서도 항상 보이도록 모든 분기에 덧붙인다
              (st.run === 'locked'
                ? '업로드 후 진행'
                : ocrComplete
                  ? `${ocrDone}/${ocrTotal}p 완료`
                  : ocrRunning
                    ? `처리 중 ${ocrDone}/${ocrTotal}p`
                    : `${ocrDone}/${ocrTotal}p`) +
              (ocrFailed > 0 ? ` · 실패 ${ocrFailed}p` : '') +
              (fileErrors > 0 ? ` · 파일 오류 ${fileErrors}` : '')
            }
            expandable={st.run !== 'locked'}
            open={isOpen('run')}
            onToggle={() => toggleStep('run')}
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="rounded-2xl border border-gray-200 bg-white p-3">
                <p className="mb-1 text-xs font-medium text-gray-600">페이지 분리</p>
                <p className="text-xl font-bold text-gray-900">
                  {detail.split_pages ?? 0}
                  <span className="text-sm font-medium text-gray-400"> / {detail.total_pages ?? 0}p</span>
                </p>
              </div>
              <OcrProgressCard
                done={ocrDone}
                total={ocrTotal}
                failed={ocrFailed}
                pending={ocrWaiting}
                processing={ocrProcessing}
                polling={polling}
                updatedAt={ocrUpdatedAt}
                retrying={ocrStatus?.retrying ?? 0}
                nextRetryAt={ocrStatus?.next_retry_at ?? null}
                className="border border-gray-200"
              />
            </div>

            {/* 읽지 못한 쪽 — 쪽마다 [다시 읽기](F9). 옛 서버에는 목록이 없어 그리지 않는다 */}
            <FailedPagesList
              failedPages={ocrStatus?.failed_pages}
              disabled={running || locked || ocrPending > 0}
              disabledReason={locked
                ? '컨펌 대기/종료 상태에서는 다시 읽을 수 없습니다'
                : ocrPending > 0 ? 'OCR이 도는 동안에는 다시 읽을 수 없습니다' : null}
              onReread={(fp) => {
                const idx = Number(fp.page_index);
                openRunAi({
                  pageRange: [idx, idx],
                  fileId: fp.file_id,
                  label: `${fp.file_name || `파일 #${fp.file_id}`} ${fp.page_no ?? idx + 1}쪽`,
                  reason: fp.reason,
                });
              }}
            />

            {/* 파일 목록 — 접이식 */}
            <button
              type="button"
              onClick={() => setFilesOpen((v) => !v)}
              className="mt-3 flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700"
            >
              {filesOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
              파일 목록 ({files.length})
            </button>
            {filesOpen && (
              files.length > 0 ? (
                <div className="mt-2 overflow-x-auto rounded-xl border">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">파일명</th>
                        <th className="px-3 py-2 text-center text-xs font-medium uppercase tracking-wider text-gray-500">종류</th>
                        <th className="px-3 py-2 text-center text-xs font-medium uppercase tracking-wider text-gray-500">분리</th>
                        <th className="px-3 py-2 text-center text-xs font-medium uppercase tracking-wider text-gray-500">OCR</th>
                        <th className="px-3 py-2 text-center text-xs font-medium uppercase tracking-wider text-gray-500">상태</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {files.map((f) => {
                        const kindInfo = getFileKindDisplay(f.kind, f.kind_label);
                        const fileStatus = getFileStatusDisplay(f.status);
                        return (
                          <tr key={f.file_id} className="border-b border-gray-100 last:border-b-0">
                            <td className="max-w-[240px] truncate px-3 py-2 text-sm text-gray-900" title={f.original_filename}>
                              {f.original_filename}
                            </td>
                            <td className="whitespace-nowrap px-3 py-2 text-center">
                              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${kindInfo.cls}`}>
                                {kindInfo.text}
                              </span>
                            </td>
                            <td className="whitespace-nowrap px-3 py-2 text-center text-sm text-gray-700">
                              {(() => {
                                const pg = filePages[f.file_id];
                                // 분모(total_pages)는 파일 원본 쪽수가 아니라 서버가 세는 값이고, 그 정의를
                                // 우리가 모른다(→ 백엔드 §3.2). 아는 사실은 '실제로 쪼개진 페이지 행이 몇 개인가'
                                // 하나뿐이라 그것만 말한다. 원본이 몇 쪽인지는 서버가 주지 않아 분모를 쓸 수 없다.
                                if (!pg || pg.truncated) return `${f.split_pages ?? 0}/${f.total_pages ?? 0}`;
                                return `${pg.indexes.length}p`;
                              })()}
                            </td>
                            <td className="whitespace-nowrap px-3 py-2 text-center text-sm text-gray-700">
                              {(() => {
                                const pg = filePages[f.file_id];
                                const done = f.extracted_pages ?? 0;
                                // 분모는 서버 집계(total_pages)가 아니라 **실제로 존재하는 페이지 행 수**를 쓴다.
                                // 2026-08-23에 7/6이 나왔는데, OCR 대상은 쪼개진 쪽이므로 분자가 분모를 넘을 수
                                // 없다 — 즉 둘 중 하나가 틀렸다는 뜻이고, 우리가 직접 셀 수 있는 쪽은 페이지 행이다.
                                // 목록을 못 읽었거나 잘렸으면 서버 값으로 폴백한다(지어내지 않는다).
                                const usable = pg && !pg.truncated;
                                const total = usable ? pg.indexes.length : (f.total_pages ?? 0);
                                // 분자가 분모를 넘으면 서버 집계를 신뢰할 수 없다는 뜻이다. 그때는 **분모로 잘라
                                // 보여 준다** — 11p짜리 파일에 「12/11」은 사람이 읽을 수 있는 문장이 아니다.
                                // 조용히 깎지 않는다는 사실은 툴팁이 서버 원값을 그대로 말해 지킨다.
                                // 분모를 늘려 맞추지는 않는다 — 그건 없는 쪽을 만들어 내는 일이다.
                                //
                                // ⚠ 배지·amber 는 여기에 붙이지 않는다. 이 어긋남은 백엔드가 고칠 때까지 모든
                                // 파일에 영구히 뜨는데 오너가 할 수 있는 일이 없다 — amber 는 이 저장소에서
                                // 「사람이 할 일이 남았다」의 색이고(④⑤ 재검수 배지), 늘 켜진 경고는 진짜 경고가
                                // 떴을 때 눈에 들지 않게 만든다. 아래 dupe 는 다르다(중복 읽기 = 비용 이중 가능).
                                const over = total > 0 && done > total;
                                const shown = over ? total : done;
                                // 같은 쪽 번호가 두 행으로 존재하면 그것도 이상 신호다(유니크 개수와 행 수가 다르다).
                                const dupe = usable && pg.count !== pg.indexes.length;
                                return (
                                  <span
                                    className={dupe ? 'text-amber-600' : undefined}
                                    title={[
                                      usable ? `쪼개진 페이지 ${pg.indexes.length}개 기준입니다(서버 집계 total_pages=${f.total_pages ?? 0}).` : null,
                                      over ? `서버 집계는 ${done}인데 페이지는 ${total}p뿐이라 ${total}p로 잘라 보여 줍니다 — 읽은 수가 쪽수를 넘을 수 없으므로 서버 집계를 신뢰할 수 없습니다. 백엔드에 확인을 요청한 상태입니다.` : null,
                                      dupe ? `같은 쪽 번호가 여러 행으로 존재합니다(행 ${pg.indexes.length} · 서로 다른 쪽 ${pg.count}).` : null,
                                    ].filter(Boolean).join('\n') || undefined}
                                  >
                                    {shown}/{total}{dupe ? ' ⚠' : ''}
                                  </span>
                                );
                              })()}
                              {(f.failed_pages ?? 0) > 0 && (
                                <span className="ml-1 text-xs text-red-500">(실패 {f.failed_pages})</span>
                              )}
                            </td>
                            <td className="whitespace-nowrap px-3 py-2 text-center">
                              {(() => {
                                const pg = filePages[f.file_id];
                                // 아직 모르거나(조회 전·실패) 쪼갠 쪽이 없거나 분리완료가 아니면 서버 상태를 그대로 쓴다.
                                // 분리중(150)에는 범위가 계속 변하므로 「분리중」이 더 정확하다.
                                if (!pg || f.status !== 200) {
                                  return (
                                    <span
                                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${fileStatus.cls}`}
                                      title={f.error_msg || undefined}
                                    >
                                      {fileStatus.text}
                                    </span>
                                  );
                                }
                                // 중간이 비었으면 부분 실행이 확정이다. 1쪽부터 이어지면 「뒤에 더 있는지」를
                                // 알 방법이 없어 단정하지 않고 범위만 말한다.
                                const partial = !pg.contiguousFromFirst;
                                // 구간이 많으면 배지가 표를 밀어낸다 — 앞 둘만 보이고 나머지는 툴팁이 갖는다
                                const label = pg.parts.length > 3
                                  ? `${pg.parts.slice(0, 2).join(', ')} 외 ${pg.parts.length - 2}구간`
                                  : pg.text;
                                return (
                                  <span
                                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                                      partial ? 'bg-amber-50 text-amber-700' : fileStatus.cls
                                    }`}
                                    title={[
                                      `읽은 쪽: ${pg.text} (${pg.count}p)`,
                                      // 서버가 준 원본 값을 그대로 보여 준다. 이 줄로 2026-08-23에 page_index가
                                      // 0부터임을 확정했고(0–10), 그래서 화면의 +1이 맞다는 것도 함께 확정됐다.
                                      // 계약에는 아직 기준이 없어(→ 백엔드 §3.1) 다른 프로젝트에서 다시 재는 자리로 남긴다.
                                      `서버 원본 page_index: ${Math.min(...pg.indexes)}–${Math.max(...pg.indexes)}`,
                                      partial
                                        ? '중간이 비어 있어 일부만 처리했습니다.'
                                        : '파일이 원래 몇 쪽인지는 서버가 알려 주지 않아, 뒤에 더 남았는지는 알 수 없습니다.',
                                      pg.truncated ? '2000쪽까지만 확인했습니다 — 그 뒤는 반영되지 않았습니다.' : null,
                                      f.error_msg || null,
                                    ].filter(Boolean).join('\n')}
                                  >
                                    {partial ? `일부 ${label}p` : `${label}p`}
                                    {pg.truncated ? ' …' : ''}
                                  </span>
                                );
                              })()}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="mt-2 rounded-xl border border-dashed py-6 text-center text-sm text-gray-500">
                  업로드된 파일이 없습니다. ① 단계에서 PDF를 추가하세요.
                </div>
              )
            )}

            <StepActions
              hint="분리는 즉시 실행되고(큰 PDF는 시간이 걸릴 수 있습니다), OCR은 백그라운드에서 순서대로 처리됩니다. 범위를 지정해 일부만 먼저 실행해 품질을 확인할 수 있습니다."
              message={actionMsg?.step === 2 ? actionMsg : null}
              aside={runAside}
            >
              {/* 파일이 하나뿐이면 고를 것이 없다 — 선택지가 하나인 드롭다운은 소음이다.
                  그때는 file_id를 보내지 않는다. 결과가 같고(전체 파일 = 그 파일), 타입도 「없는 file_id를
                  보내면 어떻게 되는지」도 계약에 없는 파라미터를 지금 잘 도는 경로에 새로 들이지 않는다. */}
              {files.length > 1 && (
                <select
                  value={runPickedFile ? runFileKey : ''}
                  onChange={(e) => setRunFileKey(e.target.value)}
                  disabled={running}
                  title="범위를 지정할 때 어느 파일에 적용할지 고릅니다. 전체 파일이면 범위가 파일마다 각각 적용됩니다."
                  className="w-56 rounded-xl border bg-white px-3 py-1.5 text-sm focus:border-primary focus:outline-none"
                >
                  <option value="">전체 파일 ({files.length}개)</option>
                  {files.map((f) => (
                    <option key={f.file_id} value={String(f.file_id)}>
                      {getFileKindDisplay(f.kind, f.kind_label).text} · {f.original_filename}
                    </option>
                  ))}
                </select>
              )}
              <input
                type="text"
                value={rangeText}
                onChange={(e) => setRangeText(e.target.value)}
                placeholder="페이지 범위 (예: 1-30, 비우면 전체)"
                disabled={running}
                className="w-56 rounded-xl border px-3 py-1.5 text-sm focus:border-primary focus:outline-none"
              />
              {/* 잠긴 이유는 툴팁이 말한다. disabled 버튼은 마우스 이벤트를 받지 않아 title이
                  뜨지 않으므로, 감싼 span에 건다(버튼에 걸면 조용히 아무 일도 안 한다). */}
              <span
                className="inline-flex"
                title={ocrPending > 0
                  ? `OCR이 ${ocrPending}페이지 남았습니다. 끝나면 다시 실행할 수 있습니다 — `
                    + '실행 중에 다시 누르면 같은 페이지를 다시 읽을 수 있어 잠가 둡니다.'
                  : runNothingToDo
                    ? '이미 읽은 쪽은 서버가 건너뜁니다 — 범위 없이 실행하면 아무 일도 일어나지 않습니다. '
                      + '아직 읽지 않은 쪽이 남았다면 범위를 적으세요(예: 11-20). 그러면 버튼이 열립니다.'
                    : undefined}
              >
                <Button
                  variant={stepBtnVariant('run')}
                  size="sm"
                  onClick={() => openRunAi()}
                  // OCR이 도는 동안은 잠근다. running 은 분리 요청이 오가는 몇 초뿐이라, 그 뒤
                  // 대기열이 도는 내내 버튼이 살아 있었다. 같은 페이지를 다시 run 하면 OCR 적재까지
                  // 건너뛰는지 우리가 모른다 — ocrApi.js 의 「이미 분리된 페이지는 스킵」이 분리까지인지
                  // 적재까지인지 미확인이다(docs/ocr-hub-v2/ocr-problem-reocr-backend-handoff.html).
                  // 적재까지 스킵이 아니면 같은 페이지를 두 번 읽어 LLM 비용이 두 번 든다.
                  disabled={running || locked || files.length === 0 || ocrPending > 0 || runNothingToDo}
                  // 도는 동안은 스피너가 돈다 — 회색으로만 죽은 버튼은 고장과 구분되지 않는다.
                  loading={running || ocrPending > 0}
                  // 스피너와 ▶는 같이 설 수 없다. 도는 동안은 스피너가 그 자리를 갖는다
                  icon={running || ocrPending > 0 ? null : <Play className="size-4" />}
                >
                  {running
                    ? '분리 중… (시간이 걸릴 수 있음)'
                    : ocrPending > 0
                      ? 'OCR 실행 중…'
                      : ocrComplete ? '다시 실행' : '분리 + OCR 실행'}
                </Button>
              </span>
            </StepActions>
          </StepRow>

          {/* ③ 문제 매칭(제본) */}
          <StepRow
            n={3}
            title="문제 매칭(제본)"
            state={st.bind}
            summary={
              st.bind === 'done'
                ? '완료'
                : st.bind === 'current'
                  ? '매칭 준비됨'
                  : bound && ocrComplete && recoveredCount > 0
                    ? `새로 읽은 쪽 ${recoveredCount}개 — 다시 매칭 필요`
                    : bound
                      ? '새 페이지 OCR 후 다시 매칭'
                      : 'OCR 완료 후'
            }
            expandable={st.bind !== 'locked'}
            open={isOpen('bind')}
            onToggle={() => toggleStep('bind')}
          >
            {reconcileTotals && (
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-2.5 text-xs text-gray-600">
                문제 매칭 결과 — 본문 <span className="font-bold text-gray-800">{reconcileTotals.body}</span>
                {' · '}정답 <span className="font-bold text-gray-800">{reconcileTotals.answer}</span>
                {' · '}풀이 <span className="font-bold text-gray-800">{reconcileTotals.solution}</span>.
{' 상세는 ④ 검수 단계에서 확인하세요.'}
              </div>
            )}
            {bound && !locked && ocrComplete && recoveredCount > 0 && (
              <p className="mt-3 rounded-xl bg-amber-50 p-2.5 text-xs leading-relaxed text-amber-800">
                ②에서 새로 읽은 쪽 {recoveredCount}개가 아직 매칭되지 않았습니다 — [다시 매칭]을 눌러야 그 쪽의 문제가 목록에 생깁니다.
                <span className="text-amber-600"> (이 화면을 연 동안 센 수입니다)</span>
              </p>
            )}
            <StepActions
              hint="페이지들을 문제 단위로 매칭합니다. 재실행해도 검수 완료분은 보존됩니다."
              message={actionMsg?.step === 3 ? actionMsg : null}
            >
              <span
                title={
                  locked
                    ? '컨펌 대기/종료 상태에서는 매칭할 수 없습니다'
                    : bindAvailable
                      ? '페이지들을 문제 단위로 매칭하고, 번호마다 본문·정답·풀이의 짝이 맞는지 점검합니다'
                      : '전체 페이지 OCR 완료 후 활성화됩니다'
                }
              >
                <Button
                  variant={stepBtnVariant('bind')}
                  size="sm"
                  // ⚠ 「매칭 뒤 새로 읽은 페이지가 없으면 잠근다」를 여기 걸었다가 걷어냈다(2026-08-23).
                  // 그 판정에 쓴 st.bind === 'done'은 「매칭 뒤 새 페이지 없음」이 아니라 「지금 대기열이
                  // 비었음」을 뜻한다 — 매칭을 마치고 대기열이 비면 언제나 참이라, 재-OCR을 끝낸 직후에도
                  // ③이 죽어 새로 읽은 페이지를 문제로 올릴 길이 사라졌다(검수 모달은 바로 그 순간
                  // 「③ 문제 매칭을 다시 실행하세요」라고 안내한다). 매칭 시점 이후에 생긴 페이지를
                  // 가려낼 신호가 서버에 없으므로, 없는 신호를 흉내 내지 않고 잠그지 않는다.
                  disabled={!bindAvailable || locked || binding}
                  loading={binding}
                  icon={<Link2 className="size-4" />}
                  onClick={handleBind}
                >
                  {bound ? '다시 매칭' : '매칭'}
                </Button>
              </span>
            </StepActions>
          </StepRow>

          {/* ④ 검수 */}
          <StepRow
            n={4}
            title="검수"
            state={st.review}
            // 재검수 대기는 여기 쓰지 않는다 — ⑤의 것이라, 완료 표시와 요약이 서로 다른 말을 하게 된다
            summary={
              st.review === 'locked'
                ? '매칭 후'
                : stats
                  ? (stats.reviewPending > 0 ? `미검수 ${stats.reviewPending}건` : `검수 완료 ${stats.reviewDone}건`)
                  : '원본과 대조해 승인'
            }
            expandable={st.review !== 'locked'}
            open={isOpen('review')}
            onToggle={() => toggleStep('review')}
          >
            {/* 문제 매칭 결과·인덱스 지도·검수 시작이 여기 모인다. 문제 목록은 검수 화면 좌측 탭으로 들어갔다 */}
            <OcrReviewSection
              projectId={projectId}
              reconcile={reconcile}
              reloadKey={problemsReloadKey}
              closed={closed}
              list={list}
              onOpenProblem={openForOcr}
              // 다른 단계는 안내가 맨 아래(StepActions)에 있지만 여기만 위로 올린다 —
              // 이 단계는 본문이 길어(문제 매칭 결과·인덱스 지도) 맨 아래 안내가 [검수 시작]보다
              // 한참 뒤에 오고, 누르기 전에 읽을 말이 누른 뒤에 읽히기 때문이다.
              hint="원본과 대조해 문제별 본문·정답·풀이를 승인/반려합니다. AI 분석이 정답·풀이를 생성하면 재검수 대기로 복귀합니다."
            />
            <StepActions
              aside={rereview > 0 ? (
                <span className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700">
                  재검수 대기 {rereview}건
                </span>
              ) : null}
            />
          </StepRow>

          {/* ⑤ AI 분석 */}
          <StepRow
            n={5}
            title="AI 분석"
            state={st.analyze}
            summary={
              st.analyze === 'locked'
                ? '매칭 후'
                : analysisStatus
                  ? `완료 ${analysisStatus.done ?? 0}/${analysisStatus.total_problems ?? 0}`
                    + ((analysisStatus.failed ?? 0) > 0 ? ` · 실패 ${analysisStatus.failed}` : '')
                    + (analysisPolling ? ' · 분석 중' : '')
                    // ④ 요약에서 뺀 만큼 여기 적는다 — 접힌 상태에서도 사람이 볼 것이 남았음을 알아야 한다
                    + (rereview > 0 ? ` · 재검수 대기 ${rereview}건` : '')
                  : 'AI 분석 대기'
            }
            expandable={st.analyze !== 'locked'}
            open={isOpen('analyze')}
            onToggle={() => toggleStep('analyze')}
          >
            {/* ② 분리+OCR과 같은 진행률 카드 — 둘 다 데몬 비동기 + 5초 폴링이라 같은 모양으로 읽힌다.
                not_started(아직 큐에 없는 문제)는 '대기'에 합치지 않는다: 대기는 데몬이 곧 가져갈 것을 뜻하고,
                not_started는 검수가 안 끝나 대상이 아닐 수도 있어 성격이 다르다(카드는 '아직 분석하지 않은 문제'로 센다). */}
            {analysisStatus && (
              <OcrProgressCard
                title="AI 분석 진행률"
                unit="건"
                notQueuedLabel="아직 분석하지 않은 문제"
                done={analysisStatus.done ?? 0}
                total={analysisStatus.total_problems ?? 0}
                failed={analysisStatus.failed ?? 0}
                pending={analysisStatus.pending ?? 0}
                processing={analysisStatus.processing ?? 0}
                polling={analysisPolling}
                updatedAt={analysisUpdatedAt}
                className="border border-gray-200"
              >
                {/* 분석 지도 — 카드의 「완료 16 / 22건」이 어느 문제인지 짚어 준다.
                    ④의 인덱스 지도가 검수 상태로 하는 일을 여기서는 분석 상태로 한다(칸을 누르면 열린다). */}
                <AnalysisMap
                  projectId={projectId}
                  // 두 신호를 합친다 — 목록 구조가 바뀔 때(problemsReloadKey)와
                  // 분석 집계가 움직일 때(analysisMapKey) 둘 다 다시 읽어야 한다.
                  reloadKey={`${problemsReloadKey}:${analysisMapKey}`}
                  onOpenProblem={openForAnalysis}
                  getAccessToken={getAccessToken}
                />
              </OcrProgressCard>
            )}
            <StepActions
              hint="생성된 문제는 검수대기로 되돌아오고, 눈으로 확인한 뒤 승인해야 등록 대상이 됩니다."
              // 갱신 표시는 진행률 카드가 'n초 전 갱신'으로 더 정확히 말한다 — 여기서는 재검수 배지만 남긴다
              aside={rereview > 0 ? (
                <span className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700">
                  재검수 대기 {rereview}건
                </span>
              ) : null}
            >
              {/* 분석을 한 번이라도 돌렸으면 다음에 할 일은 '결과를 눈으로 보고 승인'이다.
                  그래서 주 버튼은 검수 열기이고, 다시 분석은 뒤로 물린다. */}
              {analysisRan && (
                <Button
                  size="sm"
                  // 검수 화면은 단계 밖에 있다 — ⑤에서 열어도 ④를 펼치지 않는다.
                  // 필터는 걸지 않는다: 전체를 보면서 각각 승인하거나 한꺼번에 승인한다
                  // (재검수분만 보고 싶으면 목록의 '검수대기' 필터를 쓰면 된다).
                  onClick={() => { setReviewMode('analysis'); list.openReview(); }}
                  disabled={closed || list.loading || list.items.length === 0}
                  icon={<CheckSquare className="size-4" />}
                >
                  AI 결과 확인
                </Button>
              )}
              {/* 한 번 돌린 뒤의 '다시 분석'(미분석·실패분 대상)도 여기에 둔다 — 검수 모달의
                  목록 탭에만 있으면 프로젝트 화면에서는 분석 버튼이 사라진 것처럼 보인다.
                  개별 문제 재분석은 종전대로 검수 모달 액션바(결과를 보는 자리)에 있다. */}
              {analysisRan && (
                <span title="아직 분석하지 않은 문제(승인됨·실패 포함)를 분석합니다 — 이미 끝난 문제는 대상이 아닙니다. 특정 문제만 다시 돌리려면 검수 화면에서 그 문제를 여세요">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setAnalyzeAi({ mode: 'again' })}
                    loading={analyzing}
                    disabled={analyzing || analysisPolling || closed}
                    icon={<Sparkles className="size-4" />}
                  >
                    다시 분석
                  </Button>
                </span>
              )}
              {!analysisRan && (
                <span title="검수 승인된 문제의 개념·난이도를 분석하고, 정답/풀이가 없으면 생성합니다">
                  <Button
                    variant={stepBtnVariant('analyze')}
                    size="sm"
                    // 화살표로 감싼다 — 바로 넘기면 클릭 이벤트가 ocrProbIds 자리로 들어간다
                    onClick={() => setAnalyzeAi({ mode: 'first' })}
                    loading={analyzing}
                    disabled={analyzing || analysisPolling || closed}
                    icon={<Sparkles className="size-4" />}
                  >
                    AI 분석 실행
                  </Button>
                </span>
              )}
            </StepActions>
          </StepRow>

          {/* ⑥ 승격(최종 컨펌) */}
          <StepRow
            n={6}
            title="문제 등록 (최종 컨펌)"
            state={st.promote}
            // 승격 건수는 상세의 promoted_count(집계 6종)를 쓴다.
            // total_promoted는 상세에 없다 — promote/close 응답 전용 필드였다(백엔드 확인).
            summary={
              st.promote === 'locked'
                ? '매칭 후'
                : stats
                  ? (stats.reviewDone > 0 ? `등록 ${stats.promoted}/${stats.reviewDone}건` : '검수 완료 후')
                  : promoted ? `누적 등록 ${totalPromoted}건` : '등록 대기'
            }
            expandable={st.promote !== 'locked'}
            open={isOpen('promote')}
            onToggle={() => toggleStep('promote')}
          >
            {promoteResult && (
              <div className="space-y-2 rounded-xl border border-gray-200 p-3">
                <div className="text-sm text-gray-800">
                  등록 <span className="font-bold">{promoteResult.promoted_now ?? 0}</span>건
                  {' '}· 누적 <span className="font-bold">{promoteResult.total_promoted ?? 0}</span>건
                </div>
                {/* 라벨 없이 승격된 건 경고 — 허용하되 알린다(오너 결정 A-2). 자동 N번 라벨이 있어
                    보통은 0이지만, auto_labels를 끄고 돌린 경우 등을 대비해 남겨 둔다. */}
                {Number(promoteResult.no_label_count) > 0 && (
                  <div className="rounded-lg bg-amber-50 p-2 text-xs text-amber-700">
                    라벨 없이 등록 {promoteResult.no_label_count}건 — 라벨 검색에 걸리지 않습니다.
                  </div>
                )}
                <PromoteSkippedGroups skipped={promoteResult.skipped} />
                {Array.isArray(promoteResult.failed) && promoteResult.failed.length > 0 && (
                  <div className="rounded-lg bg-red-50 p-2 text-xs text-red-700">
                    <div className="font-medium">실패 {promoteResult.failed.length}건</div>
                    {promoteResult.failed.map((f) => (
                      <div key={f.ocr_prob_id ?? f.problem_number}>
                        문제 {f.problem_number ?? f.ocr_prob_id}: {f.error || '알 수 없는 오류'}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => setLabelModalOpen(true)}
                      disabled={promoting || closing || closed}
                      className="mt-1 rounded-full bg-red-100 px-2.5 py-0.5 font-medium text-red-700 hover:bg-red-200 disabled:opacity-50"
                    >
                      재시도
                    </button>
                  </div>
                )}
              </div>
            )}
            {/* 이 프로젝트가 등록한 문제 번호 — 누르면 문제 메뉴에서 연다
                (docs/ocr-hub-v2/0925_SPEC_ocr-result-links.html). 결과 카드는 이 화면에서 누른 직후에만
                서지만, 이 블록은 목록에서 읽어 새로고침 뒤에도 남는다. */}
            <PromotedProblemLinks
              projectId={projectId}
              promotedCount={stats ? stats.promoted : null}
              getAccessToken={getAccessToken}
            />
            {/* 등록 조건은 버튼 툴팁이 말한다 — 이 줄은 사람이 실제로 궁금해하는 둘만 답한다:
                「또 눌러도 되나」와 「지금 다 안 끝냈는데 눌러도 되나」. 「멱등」·「반복 실행해도
                안전」 같은 말은 무엇이 일어나지 않는지를 말해 주지 않아 뜻이 닿지 않았다. */}
            <StepActions
              hint="이미 등록한 문제는 다시 들어가지 않습니다 — 지금 등록하고, 남은 문제를 검수한 뒤 또 눌러도 됩니다. 풀이가 없는 문제도 등록됩니다."
              message={actionMsg?.step === 6 ? actionMsg : null}
            >
              <span title="검수완료(승인) + 차단 플래그 없음 + 본문·정답이 있는 문제를 등록합니다">
                <Button
                  variant={stepBtnVariant('promote')}
                  size="sm"
                  onClick={() => setLabelModalOpen(true)}
                  loading={promoting}
                  disabled={promoting || closing || closed}
                  icon={<CheckSquare className="size-4" />}
                >
                  {promoted ? '다시 등록' : '문제 등록 실행 (최종 컨펌)'}
                </Button>
              </span>
            </StepActions>
          </StepRow>

          {/* ⑦ 프로젝트 종료 */}
          <StepRow
            n={7}
            title="프로젝트 종료"
            state={st.close}
            summary={
              closed
                ? (detail?.result_paper_id != null ? `종료됨 · 시험지 #${detail.result_paper_id}` : '종료됨')
                : st.close === 'locked' ? '매칭 후' : '등록 후 마감'
            }
            expandable={st.close !== 'locked'}
            open={isOpen('close')}
            onToggle={() => toggleStep('close')}
          >
            {/* 종료하며 만든 시험지 — 시험지 v2 표 한 줄. 지워졌으면 링크를 닫고 그 사실을 말한다(같은 설계 문서 §6) */}
            <ResultPaperRow
              paperId={detail?.result_paper_id ?? null}
              closed={closed}
              getAccessToken={getAccessToken}
            />
            {closedUnregistered && detail?.result_paper_id == null && (
              <p className="mt-2 text-xs text-amber-700">
                등록 없이 종료되어 시험지가 만들어지지 않았습니다 — 검수·분석한 {unregisteredReviewed}문제는 문제은행에 들어가지 않았습니다.
              </p>
            )}
            <StepActions
              // 종료 뒤에는 미래형 안내(「종료하면 … 만들어지고」)가 정상 종료처럼 읽힌다 — 지금 상태를 말한다
              hint={closed
                ? '종료된 프로젝트입니다 — 업로드·실행·매칭·분석·등록이 모두 막혀 있습니다. 등록된 문제는 [문제관리]에서 계속 관리됩니다.'
                  + (detail?.result_paper_id == null ? ' 시험지 없이 닫혔으면 [종료 취소]로 다시 열 수 있습니다.' : '')
                : '종료하면 등록한 문제로 시험지 한 장이 만들어지고, 업로드·실행·매칭·분석·등록이 모두 차단됩니다. 등록된 문제는 [문제관리]에서 계속 관리됩니다.'}
              message={actionMsg?.step === 7 ? actionMsg : null}
            >
              {closed && detail?.result_paper_id == null && (
                <Button size="sm" variant="primary-outline" onClick={handleReopen} loading={reopening} disabled={reopening}>
                  종료 취소
                </Button>
              )}
              <Button
                size="sm"
                variant="primary-outline"
                onClick={() => setCloseModalOpen(true)}
                loading={closing}
                disabled={promoting || closing || closed}
              >
                프로젝트 종료
              </Button>
            </StepActions>
          </StepRow>

        </div>

        {/* 프로젝트 기본 지시 — 하단 접이식.
            단계(StepRow)가 아니라 단계 밖에 두는 이유는 위 promptOpen 상태 선언부의 주석에 있다.
            얼굴은 바로 아래 [LLM 사용량] 블록을 그대로 베꼈다 — 같은 층위(단계 밖 프로젝트 속성)에
            서는 둘이라 크기·색·간격·아이콘을 갈라 놓을 이유가 없다.
            머리줄에 지금 걸린 값을 흘려 보인다: 접어 둔 채로도 「걸려 있는가」를 알 수 있어야 한다.
            머리 버튼만 w-full 로 다르다 — 흘려 보이는 값을 truncate 하려면 줄 끝이 정해져야 한다.
            글자 크기·색·간격·아이콘은 사용량 블록과 같은 값이다(StepRow 의 summary 와도 같은 수법). */}
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setPromptOpen((v) => !v)}
            className="flex w-full items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700"
          >
            {/* 값은 아래 [LLM 사용량]과 같은 3.5 다. 표기만 size-* 인 것은 tailwindcss/enforces-shorthand
                경고를 새로 늘리지 않으려는 것으로, 나오는 CSS 는 h-3.5 w-3.5 와 같다. */}
            {promptOpen ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
            프로젝트 기본 지시
            <span className="ml-2 min-w-0 truncate font-normal text-gray-400" title={serverPrompt || undefined}>
              {serverPrompt ? `「${serverPrompt}」` : '없음'}
            </span>
          </button>
          {promptOpen && (
            <div className="mt-2 rounded-2xl border border-gray-200 bg-white p-3 shadow-sm">
              {/* 적용 범위를 사실대로 적는다 — 세 곳에만 걸리고 그림에는 걸리지 않는다
                  (BE-20260829-reply-fe-0828.md §4-2 5-4). */}
              <p className="mb-2 text-xs text-gray-400">
                ② 분리 + OCR · 다시 OCR · ⑤ AI 분석에서 지시를 비우고 실행하면 이 문장이 대신 쓰입니다.
                그림 재요청·명세 변환에는 쓰이지 않습니다.
              </p>
              <AiPromptInput
                label="프로젝트 기본 지시"
                value={promptText}
                onChange={setPromptText}
                rows={3}
                placeholder="예: 이 교재는 선지가 6개다 · 표는 두 단으로 읽어라"
                disabled={promptSaving || closed}
              />
              {promptMsg && (
                <p className={`mt-1 text-xs ${promptMsg.type === 'error' ? 'text-red-600' : 'text-blue-600'}`}>
                  {promptMsg.text}
                </p>
              )}
              {closed && (
                <p className="mt-1 text-xs text-gray-400">종료된 프로젝트라 고칠 수 없습니다.</p>
              )}
              <div className="mt-2 flex flex-wrap items-center justify-end gap-2">
                {/* [해제]는 값이 걸려 있을 때만 낸다 — 눌러도 아무 일이 없는 버튼을 두지 않는다 */}
                {serverPrompt && (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => saveDefaultPrompt('')}
                    disabled={promptSaving || closed}
                  >
                    해제
                  </Button>
                )}
                <Button
                  size="sm"
                  onClick={() => saveDefaultPrompt(promptText)}
                  loading={promptSaving}
                  disabled={promptSaving || closed || promptText.trim() === serverPrompt.trim()}
                >
                  저장
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* LLM 사용량 — 하단 접이식 */}
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setUsageOpen((v) => !v)}
            className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700"
          >
            {usageOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            LLM 사용량·비용
          </button>
          {usageOpen && (
            <div className="mt-2">
              <OcrUsageCard projectId={projectId} />
            </div>
          )}
        </div>
      </div>

      {/* 검수 화면 — 어느 단계에도 속하지 않는다. ④에서도 ⑤에서도 같은 모달을 열고,
          여는 쪽은 그 단계를 펼치지 않는다. 좌측에서 원본과 목록을 탭으로 오간다. */}
      {/* 등록 전 라벨 확인 — ⑥의 [문제 등록 실행]/[다시 등록]과 결과 카드의 [재시도]가 모두 이 창을 연다.
          창이 확인창을 겸하므로 handlePromote 를 부르는 자리는 이 창의 버튼 하나뿐이다. */}
      <PromoteLabelModal
        isOpen={labelModalOpen}
        onClose={() => { setLabelModalOpen(false); promoteFromCloseRef.current = false; }}
        lockWarning={firstRegisterLockWarning}
        projectId={projectId}
        keywords={detail?.keywords}
        getAccessToken={getAccessToken}
        onPromote={handlePromote}
        promoting={promoting}
        onKeywordsSaved={refreshDetail}
      />

      {/* 종료하며 시험지로 저장 — 종료 호출을 창이 갖는다(시험지 → 종료 순서를 잇기 위해) */}
      <ClosePaperModal
        isOpen={closeModalOpen}
        onClose={() => setCloseModalOpen(false)}
        projectId={projectId}
        projectName={detail?.name}
        stats={stats}
        getAccessToken={getAccessToken}
        onClosed={handleClosed}
        // 「N건 등록 창 열기」 — 종료 창을 닫고 ⑥ 을 펼쳐 그 등록 창을 연다(오너 결정 2026-09-25).
        // 등록이 끝나면 handlePromote 가 「이제 ⑦ 에서 종료하세요」를 덧붙인다.
        onOpenPromote={() => {
          setCloseModalOpen(false);
          promoteFromCloseRef.current = true;
          setOpenKey('promote');
          setLabelModalOpen(true);
        }}
      />

      <OcrReviewModal
        isOpen={list.selectedProbId != null}
        problemId={list.selectedProbId}
        mode={reviewMode}
        projectId={projectId}
        refreshKey={problemsReloadKey}
        items={list.items}
        page={list.page}
        totalPages={list.totalPages}
        navPending={list.navPending}
        listError={list.loadError}
        closed={closed}
        // 페이지 재실행이 쓰는 둘 — 파일 목록은 file_id를 찾기 위한 것(페이지 쪽에는 uuid·이름만 있다),
        // locked는 컨펌 대기(400)부터 run이 423이라 미리 막기 위한 것이다.
        projectFiles={files}
        projectLocked={locked}
        // 공통 라벨(프로젝트 키워드) — 검수 모달이 「등록되면 이 문제에 붙을 라벨」을 다 보여주려면
        // 프로젝트 쪽 값이 필요하다. 문제 상세 응답에는 없다(프로젝트 메타다).
        projectKeywords={detail?.keywords}
        // 프로젝트 이름 — 검수 창 제목이 「어느 프로젝트인가」를 번호와 함께 말한다(패널 헤더와 같은 값)
        projectName={detail?.name}
        // 프로젝트 기본 지시 — 검수 모달의 AI 요청 창 셋(다시 OCR · 이 문제 다시 분석 · 전체 다시 분석)이
        // 「비우면 이것이 쓰인다」를 말하려면 필요한데, 문제 상세 응답에는 없다(프로젝트 메타다).
        // projectKeywords 와 같은 이유·같은 자리라 나란히 둔다. 그리는 것은 모달 쪽 몫이다.
        projectDefaultPrompt={detail?.default_prompt}
        onRerunPage={handleRerunPage}
        onNavigate={list.select}
        onStepPage={list.stepPage}
        onClose={list.closeReview}
        onChanged={list.reloadAfterMutate}
        onReviewed={handleReviewed}
        checkedIds={list.checkedIds}
        onChecked={list.markChecked}
        listLoading={list.loading}
        filter={list.filter}
        onFilterChange={list.setFilter}
        totalItems={list.totalItems}
        onPageChange={list.goToPage}
        onClearChecked={list.clearChecked}
        onRetryList={list.reload}
        onApproveAll={list.approveAll}
        approving={list.approving}
        approveResult={list.approveResult}
        canApprove={!closed}
        // 모달 안에서 부른 결과는 모달 안에서 말해야 한다 — 패널 쪽 안내는 모달에 가려 보이지 않는다
        // 두 곳에서 부른다 — 목록 탭의 [다시 분석](인자 없음, 기본 대상 전체)과
        // 액션바의 [다시 분석](그 문제 id 하나). 결과를 말하는 자리가 서로 달라 갈라 둔다.
        onReanalyze={async (ocrProbIds, userPrompt) => {
          const targeted = Array.isArray(ocrProbIds) && ocrProbIds.length > 0;
          // 확인은 이제 모달 쪽 AiRunModal 이 한다(지정·전체 둘 다) — 여기서 다시 묻지 않는다.
          if (!targeted) list.setApproveResult(null);
          const r = await handleAnalyze(ocrProbIds, userPrompt);
          if (r && !targeted) list.setApproveResult({ text: r.message, tone: r.ok ? 'green' : 'red' });
          return r; // 지정 재분석은 모달이 토스트로 말한다
        }}
        analyzeLabel={analysisRan ? '다시 분석' : 'AI 분석'}
        reanalyzing={analyzing || analysisPolling}
      />

      {/* ② 분리 + OCR — 종전 확인창 둘(범위 비었을 때·파일 여럿일 때)이 warning 으로 들어온다.
          이 패널은 모달이 아니라 페이지 안 패널이라 창이 겹치지 않는다.

          안내 둘째 문장은 회신 §4-2 6-2 의 꼬리다 — 「OCR run·reocr 의 지시는 본문/풀이/정답 읽기
          프롬프트에만 붙고 **그림 후처리(figure pass)에는 붙지 않습니다**」. run 이 걸리는 두 자리
          중 다른 하나(검수 모달의 [이 문제 다시 OCR])에는 이미 같은 문장이 들어가 있어 여기에도
          맞춘다 — 같은 한계가 걸리는 창이 한쪽만 말하면, 말 안 한 쪽에서 사람이 「원은 8개다」를
          써 넣고 그림이 안 고쳐지는 것을 고장으로 읽는다.
          그림에 대한 지시를 받는 창은 따로 있다(검수 모달의 그림 창 [AI 요청]).
          아래 ⑤ AI 분석 창에는 이 문장을 넣지 않는다 — 회신이 한계를 건 것은 run·reocr 둘뿐이고,
          없는 한계를 적으면 그것도 거짓이다. */}
      <AiRunModal
        isOpen={runAi != null}
        title={runAi?.preset ? '이 쪽 다시 읽기' : '분리 + OCR 실행'}
        summary={(
          <>
            {runAi?.preset
              ? <>「{runAi.preset.label}」 한 쪽만 다시 AI 로 읽습니다. 읽기가 끝나면 ③ 매칭을 다시 해야 그 쪽의 문제가 목록에 생깁니다.</>
              : <>PDF 를 쪽으로 쪼개고 각 쪽을 AI 로 읽습니다. 분리는 즉시, OCR 은 대기열에서 차례로 처리됩니다.</>}
            {' '}아래 지시는 본문·풀이·정답을 읽는 데만 쓰이고, 그림 변환에는 적용되지 않습니다.
            <DefaultPromptNotice prompt={detail?.default_prompt} variant="run" />
          </>
        )}
        // 대상 쪽에 전에 걸어 둔 지시(F10) — 비우고 실행하면 서버가 그대로 쓴다. 지우려면 여기서 고른다.
        // 모르면(쪽 목록을 아직 못 읽음·옛 서버) 아무 말도 하지 않는다
        options={Array.isArray(runAi?.prompts) && runAi.prompts.length > 0 ? (
          <div className="rounded-xl border border-gray-200 p-3 text-xs text-gray-700">
            <p className="font-medium">{runAi.preset ? '이 쪽' : '대상 쪽'}에 전에 걸어 둔 지시가 있습니다</p>
            <ul className="mt-1 space-y-0.5 text-gray-600">
              {runAi.prompts.slice(0, 3).map((x) => (
                <li key={x.prompt} className="break-words">
                  「{x.prompt.length > 60 ? `${x.prompt.slice(0, 60)}…` : x.prompt}」{runAi.preset ? '' : ` ${x.count}쪽`}
                </li>
              ))}
              {runAi.prompts.length > 3 && <li>외 {runAi.prompts.length - 3}가지</li>}
            </ul>
            <label className="mt-2 flex items-center gap-2">
              <input
                type="checkbox"
                checked={runClearPrompt}
                onChange={(e) => setRunClearPrompt(e.target.checked)}
                disabled={running}
                className="accent-primary"
              />
              지시를 비우고 실행하면 이 지시도 지웁니다
            </label>
            <p className="mt-1 text-[11px] text-gray-400">
              체크하지 않으면 전에 건 지시가 그대로 쓰입니다. 아래에 새 지시를 적으면 그것으로 바뀝니다.
            </p>
          </div>
        ) : null}
        warning={runAi?.warning || null}
        placeholder="예: 이 교재는 선지가 6개다 · 표는 두 단으로 읽어라"
        confirmText="실행"
        busy={running}
        onConfirm={handleRun}
        onClose={() => setRunAi(null)}
      />

      {/* ⑤ AI 분석 — 종전 confirmAnalyze 의 문구가 그대로 안내·경고가 됐다. */}
      <AiRunModal
        isOpen={analyzeAi != null}
        title={analyzeAi?.mode === 'again' ? '다시 분석' : 'AI 분석 실행'}
        summary={(
          <>
            {ANALYZE_SUMMARY[analyzeAi?.mode === 'again' ? 'again' : 'first']}
            <DefaultPromptNotice prompt={detail?.default_prompt} />
          </>
        )}
        warning={ANALYZE_WARNING}
        placeholder="예: 고1 과정 개념만 쓴다 · 풀이는 두 줄 이내로"
        confirmText="실행"
        busy={analyzing}
        onConfirm={(userPrompt) => { setAnalyzeAi(null); handleAnalyze(null, userPrompt); }}
        onClose={() => setAnalyzeAi(null)}
      />
    </div>
  );
}

export default ProjectDetailPanel;
